<template>
  <v-container class="app-container">
  <div class="summary-view">
    <!-- Auswahl-Card mit Glasmorphismus-Effekt -->
    <v-card class="summary-card">
      <!-- Header mit Titel und Tabs -->
      <div class="summary-header">
        <div class="title-container">
          <v-icon color="primary" size="24" class="mr-2"
            >mdi-chart-timeline-variant</v-icon
          >
          <h2 class="card-title">Zusammenfassung</h2>
        </div>
      </div>

      <!-- Tab-Navigation -->
      <div class="summary-tabs-wrap">
        <v-tabs
          v-model="activeTab"
          color="success"
          class="summary-tabs"
          density="comfortable"
          slider-color="success"
        >
          <v-tab value="zeitraum">
            <v-icon start>mdi-calendar-month-outline</v-icon>
            Zeitraum Auswertung
          </v-tab>
          <v-tab value="monat">
            <v-icon start>mdi-calculator-variant</v-icon>
            Zeitraum Berechnung
          </v-tab>
        </v-tabs>
      </div>

      <v-divider class="summary-divider"></v-divider>

      <!-- Filter-Bereich -->
      <v-card-text class="filter-section">
        <v-row align="center">
          <v-col cols="12" sm="5">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              item-title="label"
              item-value="value"
              label="Monat auswählen"
              return-object
              variant="outlined"
              density="comfortable"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              hide-details
              class="filter-select"
              @update:model-value="fetchZusammenfassungen"
              prepend-inner-icon="mdi-calendar-month"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="5">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="Jahr auswählen"
              variant="outlined"
              density="comfortable"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              hide-details
              class="filter-select"
              @update:model-value="fetchZusammenfassungen"
              prepend-inner-icon="mdi-calendar-star"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="2" class="d-flex justify-end">
            <v-btn
              v-if="activeTab === 'zeitraum'"
              color="success"
              variant="elevated"
              prepend-icon="mdi-file-pdf-box"
              class="export-btn"
              @click="exportPdf"
            >
              PDF Export
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tab: Zeitraum Auswertung -->
    <v-expand-transition>
      <div v-if="activeTab === 'zeitraum'" ref="pdfContent">
        <v-card class="summary-result-card mt-4">
          <div class="result-header">
            <div class="title-container">
              <v-icon color="primary" size="24" class="mr-2"
                >mdi-clipboard-text-outline</v-icon
              >
              <h2 class="card-title">
                Auswertung: {{ currentMonthLabel }} {{ selectedYear }} –
                {{ currentUserName }} ({{ authStore.user.mitarbeiternummer }})
              </h2>
            </div>
          </div>

          <v-divider class="summary-divider"></v-divider>

          <v-card-text v-if="zusammenfassungen" class="result-content">
            <!-- Gerätetypen-Übersicht -->
            <div class="device-section">
              <h3 class="section-heading">
                <v-icon color="success" class="mr-2">mdi-devices</v-icon>
                Gerätetypen
              </h3>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  v-for="geraetetyp in displayedDeviceTypes"
                  :key="geraetetyp"
                >
                  <v-card class="device-card">
                    <div class="device-header">
                      <v-icon color="success" class="mr-2">mdi-laptop</v-icon>
                      <h4 class="device-title">{{ geraetetyp }}</h4>
                    </div>

                    <!-- Instandsetzungen -->
                    <v-card-text class="device-content">
                      <div
                        v-if="groupedInstandsetzungen[geraetetyp]"
                        class="stat-group"
                      >
                        <div class="stat-label">
                          <v-icon color="success" size="small" class="mr-1"
                            >mdi-wrench</v-icon
                          >
                          Instandsetzungen
                        </div>
                        <div
                          v-for="item in groupedInstandsetzungen[geraetetyp]"
                          :key="item.reparaturtyp"
                          class="stat-item"
                        >
                          <span class="stat-name">{{ item.reparaturtyp }}</span>
                          <span class="stat-value"
                            >{{ item.stueckzahl }} Stk.</span
                          >
                        </div>
                      </div>

                      <!-- Retouren -->
                      <div
                        v-if="groupedRetouren[geraetetyp]"
                        class="stat-group"
                      >
                        <div class="stat-label">
                          <v-icon color="error" size="small" class="mr-1"
                            >mdi-package-variant</v-icon
                          >
                          Retourenbearbeitung
                        </div>
                        <div
                          v-for="item in groupedRetouren[geraetetyp]"
                          :key="item.retourentyp"
                          class="stat-item"
                        >
                          <span class="stat-name">{{ item.retourentyp }}</span>
                          <span class="stat-value"
                            >{{ item.stueckzahl }} Stk.</span
                          >
                        </div>
                      </div>

                      <!-- Kategoriezusammenfassung -->
                      <div
                        v-if="deviceTypeCategorySummary[geraetetyp] && deviceTypeCategorySummary[geraetetyp].total > 0"
                        class="stat-group category-summary"
                      >
                        <v-divider class="my-2"></v-divider>
                        <div class="stat-item summary-total">
                          <span class="stat-name"><strong>Gesamt</strong></span>
                          <span class="stat-value"><strong>{{ deviceTypeCategorySummary[geraetetyp].total }} Stk.</strong></span>
                        </div>
                      </div>

                      <div
                        v-if="
                          !groupedInstandsetzungen[geraetetyp] &&
                          !groupedRetouren[geraetetyp]
                        "
                        class="no-data"
                      >
                        <v-icon color="grey" size="20" class="mr-1"
                          >mdi-information-outline</v-icon
                        >
                        Keine Daten verfügbar
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Gesamtsumme über alle Gerätetypen -->
              <v-card v-if="overallCategoryTotal > 0" class="overall-total-card mt-4">
                <v-card-text class="overall-total-content">
                  <div class="overall-total-item">
                    <div class="overall-total-label">
                      <v-icon color="success" class="mr-2">mdi-sigma</v-icon>
                      <strong>Gesamtsumme aller Kategorien</strong>
                    </div>
                    <div class="overall-total-value">
                      <strong>{{ overallCategoryTotal }} Stk.</strong>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Nebentätigkeiten-Sektion -->
            <v-card
              v-if="
                zusammenfassungen.nebentaetigkeiten &&
                zusammenfassungen.nebentaetigkeiten.length > 0
              "
              class="nebentaetigkeiten-card mt-4"
            >
              <div class="activity-header">
                <v-icon color="amber" class="mr-2">mdi-clock-outline</v-icon>
                <h3 class="activity-title">Nebentätigkeiten</h3>
              </div>

              <v-card-text class="activity-content">
                <div
                  v-for="group in Object.values(groupedNebentaetigkeitenWithDetails)"
                  :key="group.kommentar"
                  class="activity-group"
                >
                  <!-- Gruppierter Header (klickbar zum Expandieren) -->
                  <div
                    class="activity-item activity-group-header"
                    @click="toggleNebentaetigkeit(group.kommentar)"
                  >
                    <div class="activity-details">
                      <v-icon
                        size="small"
                        color="amber"
                        class="mr-2"
                      >
                        {{ expandedNebentaetigkeiten.has(group.kommentar) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                      <v-icon size="small" color="amber" class="mr-2"
                        >mdi-clock-time-four-outline</v-icon
                      >
                      <span class="activity-name">{{ group.kommentar }}</span>
                      <span class="activity-count">({{ group.items.length }})</span>
                    </div>
                    <span class="activity-duration">{{
                      formatDuration(group.totalDauer)
                    }}</span>
                  </div>

                  <!-- Expandierte Einzeleinträge -->
                  <v-expand-transition>
                    <div v-if="expandedNebentaetigkeiten.has(group.kommentar)" class="activity-items-list">
                      <div
                        v-for="item in group.items"
                        :key="item.id"
                        class="activity-item-detail"
                      >
                        <div class="activity-item-info">
                          <v-icon size="x-small" color="grey-lighten-1" class="mr-2"
                            >mdi-circle-small</v-icon
                          >
                          <span class="activity-date">
                            {{ new Date(item.startzeit).toLocaleDateString('de-DE') }}
                          </span>
                          <span class="activity-time">
                            {{ new Date(item.startzeit).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }} -
                            {{ new Date(item.endzeit).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) }}
                          </span>
                          <span class="activity-duration-small">
                            {{ formatDuration(item.dauer) }}
                          </span>
                        </div>
                        <div class="activity-item-actions">
                          <v-btn
                            icon="mdi-pencil"
                            size="x-small"
                            variant="text"
                            color="blue"
                            @click.stop="openEditNebentaetigkeit(item)"
                          ></v-btn>
                          <v-btn
                            icon="mdi-delete"
                            size="x-small"
                            variant="text"
                            color="red"
                            @click.stop="openDeleteNebentaetigkeit(item.id)"
                          ></v-btn>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Gesamtzeit -->
                <div class="activity-total">
                  <div class="total-row">
                    <div class="total-label">
                      <v-icon color="amber" class="mr-1"
                        >mdi-clock-check-outline</v-icon
                      >
                      <span>Gesamtzeit Nebentätigkeiten:</span>
                    </div>
                    <div class="total-primary">{{ totalDurationShort }}</div>
                  </div>
                  <div class="total-row">
                    <div class="total-empty"></div>
                    <div class="total-secondary">
                      {{ totalDurationDetailed }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Keine Daten Message -->
            <v-card v-if="!hasData" class="no-data-card mt-4">
              <v-card-text class="no-data-content">
                <v-icon size="48" color="grey" class="mb-3"
                  >mdi-database-off</v-icon
                >
                <h3>Keine Daten verfügbar</h3>
                <p>
                  Für den ausgewählten Zeitraum wurden keine Daten gefunden.
                </p>
              </v-card-text>
            </v-card>
          </v-card-text>

          <!-- Loading Skeleton -->
          <v-card-text v-else class="result-content">
            <v-skeleton-loader
              type="article, list-item-avatar-three-line@2"
            ></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </div>
    </v-expand-transition>

    <!-- Tab: Zeitraum Berechnung -->
    <v-expand-transition>
      <div v-if="activeTab === 'monat'">
        <v-card class="summary-result-card mt-4">
          <div class="result-header">
            <div class="title-container">
              <v-icon color="primary" size="24" class="mr-2"
                >mdi-currency-eur</v-icon
              >
              <h2 class="card-title">
                Berechnung: {{ currentMonthLabel }} {{ selectedYear }} –
                {{ currentUserName }} ({{ authStore.user.mitarbeiternummer }})
              </h2>
            </div>
          </div>

          <v-divider class="summary-divider"></v-divider>

          <div class="worktime-info-badge" v-if="userWorkTimeSettings.lastUpdated">
            <v-icon size="small" color="info" class="mr-1">mdi-clock-outline</v-icon>
            <span>
              Berechnung basiert auf Ihrer persönlichen Arbeitszeit:
              <strong>{{ userWorkTimeSettings.weeklyHours }} Std./Woche</strong> bei
              <strong>{{ userWorkTimeSettings.workdaysPerWeek }} Tagen/Woche</strong>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" size="small" color="info" class="ml-1">mdi-information-outline</v-icon>
                </template>
                <span>
                  Arbeitszeit pro Tag: {{ hoursPerWorkday.toFixed(2) }} Std.<br>
                  Zuletzt aktualisiert: {{ new Date(userWorkTimeSettings.lastUpdated).toLocaleDateString() }}
                </span>
              </v-tooltip>
            </span>
          </div>

          <v-card-text v-if="zusammenfassungen" class="result-content">
            <!-- Gerätetypen mit Preisen -->
            <div class="device-section">
              <h3 class="section-heading">
                <v-icon color="success" class="mr-2"
                  >mdi-calculator-variant</v-icon
                >
                Berechnete Positionen
              </h3>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  v-for="geraetetyp in displayedDeviceTypesWithPrice"
                  :key="geraetetyp"
                >
                  <v-card class="device-card calculation-card">
                    <div class="device-header">
                      <v-icon color="success" class="mr-2">mdi-laptop</v-icon>
                      <h4 class="device-title">{{ geraetetyp }}</h4>
                    </div>

                    <!-- Instandsetzungen mit Preisen -->
                    <v-card-text class="device-content">
                      <div
                        v-if="
                          groupedInstandsetzungen[geraetetyp] &&
                          groupedInstandsetzungen[geraetetyp].some((item) =>
                            getPreis(geraetetyp, item.reparaturtyp)
                          )
                        "
                        class="stat-group"
                      >
                        <div class="stat-label">
                          <v-icon color="success" size="small" class="mr-1"
                            >mdi-wrench</v-icon
                          >
                          Instandsetzungen
                        </div>

                        <div
                          v-for="item in groupedInstandsetzungen[
                            geraetetyp
                          ].filter((item) =>
                            getPreis(geraetetyp, item.reparaturtyp)
                          )"
                          :key="item.reparaturtyp"
                          class="stat-item"
                        >
                          <span class="stat-name">{{ item.reparaturtyp }}</span>
                          <span class="stat-value price">
                            {{ item.stueckzahl }} Stk.
                            <template
                              v-if="getPreis(geraetetyp, item.reparaturtyp)"
                            >
                              <span class="price-unit"
                                >@{{
                                  getPreis(geraetetyp, item.reparaturtyp).preis
                                }}€</span
                              >
                              <span class="price-total"
                                >=
                                {{
                                  (
                                    item.stueckzahl *
                                    getPreis(geraetetyp, item.reparaturtyp)
                                      .preis
                                  ).toFixed(2)
                                }}€
                              </span>
                            </template>
                          </span>
                        </div>
                      </div>

                      <!-- Retouren mit Preisen -->
                      <div
                        v-if="
                          groupedRetouren[geraetetyp] &&
                          groupedRetouren[geraetetyp].some((item) =>
                            getPreis(geraetetyp, item.retourentyp)
                          )
                        "
                        class="stat-group"
                      >
                        <div class="stat-label">
                          <v-icon color="error" size="small" class="mr-1"
                            >mdi-package-variant</v-icon
                          >
                          Retourenbearbeitung
                        </div>

                        <div
                          v-for="item in groupedRetouren[geraetetyp].filter(
                            (item) => getPreis(geraetetyp, item.retourentyp)
                          )"
                          :key="item.retourentyp"
                          class="stat-item"
                        >
                          <span class="stat-name">{{ item.retourentyp }}</span>
                          <span class="stat-value price">
                            {{ item.stueckzahl }} Stk.
                            <template
                              v-if="getPreis(geraetetyp, item.retourentyp)"
                            >
                              <span class="price-unit"
                                >@{{
                                  getPreis(geraetetyp, item.retourentyp).preis
                                }}€</span
                              >
                              <span class="price-total"
                                >=
                                {{
                                  (
                                    item.stueckzahl *
                                    getPreis(geraetetyp, item.retourentyp).preis
                                  ).toFixed(2)
                                }}€
                              </span>
                            </template>
                          </span>
                        </div>
                      </div>

                      <!-- Gerätetypbezogener Gesamtverdienst -->
                      <div
                        v-if="
                          deviceTypeEarnings[geraetetyp] &&
                          deviceTypeEarnings[geraetetyp].total > 0
                        "
                        class="device-total"
                      >
                        <div class="total-label">
                          <v-icon color="success" size="small" class="mr-1"
                            >mdi-cash-multiple</v-icon
                          >
                          Gesamtverdienst:
                        </div>
                        <div class="total-value">
                          {{ deviceTypeEarnings[geraetetyp].total.toFixed(2) }}€
                        </div>
                      </div>

                      <div v-else class="no-data">
                        <v-icon color="grey" size="20" class="mr-1"
                          >mdi-information-outline</v-icon
                        >
                        Keine Preisdaten verfügbar
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Gesamter Verdienst -->
            <v-card
              v-if="overallEarnings.total > 0"
              class="overall-earnings-card mt-4"
            >
              <div class="earnings-header">
                <v-icon color="success" class="mr-2">mdi-cash-register</v-icon>
                <h3 class="earnings-title">Gesamtverdienst</h3>
              </div>

              <v-card-text class="earnings-content">
                <!-- BEREICH 1: AKTUELLE VERDIENSTE -->
                <div class="content-section">
                  <div class="section-title">
                    <v-icon color="success" class="mr-2"
                      >mdi-cash-register</v-icon
                    >
                    Aktueller Verdienst
                  </div>

                  <div class="earnings-item">
                    <div class="earnings-label">
                      <v-icon color="success" size="small" class="mr-1"
                        >mdi-wrench</v-icon
                      >
                      Instandsetzungen insgesamt:
                    </div>
                    <div class="earnings-value">
                      {{ overallEarnings.instand.toFixed(2) }}€
                    </div>
                  </div>

                  <div class="earnings-item">
                    <div class="earnings-label">
                      <v-icon color="error" size="small" class="mr-1"
                        >mdi-package-variant</v-icon
                      >
                      Retourenbearbeitung insgesamt:
                    </div>
                    <div class="earnings-value">
                      {{ overallEarnings.retour.toFixed(2) }}€
                    </div>
                  </div>

                  <div class="earnings-total">
                    <div class="total-label">
                      <v-icon color="success" class="mr-1">mdi-cash</v-icon>
                      Gesamt:
                    </div>
                    <div class="total-value grand-total">
                      {{ overallEarnings.total.toFixed(2) }}€
                    </div>
                  </div>
                </div>

                <!-- BEREICH 2: 3-Monats-Durchschnitt -->
                <div class="content-section">
                  <div class="section-title">
                    <v-icon color="success" class="mr-2">mdi-chart-line</v-icon>
                    3-Monats-Durchschnitt
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <v-icon
                          v-bind="props"
                          size="small"
                          class="ml-1"
                          color="success"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span
                        >Durchschnitt der letzten 3 Monate ({{
                          previousMonthsText
                        }})</span
                      >
                    </v-tooltip>
                  </div>

                  <div class="average-content" v-if="!loadingHistory">
                    <div
                      class="earnings-item average-item"
                      v-for="(month, index) in historyData"
                      :key="index"
                    >
                      <div class="month-label">
                        <v-icon size="small" color="success" class="mr-1"
                          >mdi-calendar-month</v-icon
                        >
                        {{ month.label }}:
                      </div>
                      <div class="month-value">
                        {{ month.total.toFixed(2) }}€
                      </div>
                    </div>

                    <div class="average-total">
                      <div class="total-label">
                        <v-icon color="success" class="mr-1"
                          >mdi-chart-areaspline</v-icon
                        >
                        Durchschnitt:
                      </div>
                      <div class="total-value average-value">
                        {{ averageEarnings.toFixed(2) }}€
                      </div>
                    </div>
                  </div>

                  <div class="average-loading" v-else>
                    <v-progress-circular
                      indeterminate
                      color="success"
                      size="20"
                      width="2"
                    ></v-progress-circular>
                    <span class="ml-2">Berechne Durchschnitt...</span>
                  </div>
                </div>

                <!-- BEREICH 3: ARBEITSZEITBERECHNUNG -->
                <div v-if="sideActivitiesHours > 0" class="content-section">
                  <div class="section-title">
                    <v-icon color="amber" class="mr-2"
                      >mdi-calculator-variant-outline</v-icon
                    >
                    Arbeitszeitberechnung (3-Monats-Überblick)
                  </div>

                  <!-- Arbeitszeitberechnung Details mit integrierten Abwesenheiten -->
                  <div class="workdays-calculation">
                    <v-row>
                      <v-col cols="12" md="6">
                        <!-- Linke Spalte: Abwesenheitsübersicht -->
                        <div class="absence-overview">
                          <div class="absence-header">
                            <span class="absence-title">
                              <v-icon color="primary" size="small" class="mr-2"
                                >mdi-calendar-clock</v-icon
                              >
                              Abwesenheiten im 3-Monats-Zeitraum
                            </span>

                            <v-btn
                              v-if="!loadingCalendarData"
                              @click="loadThreeMonthCalendarData"
                              variant="text"
                              color="success"
                              size="small"
                              :disabled="loadingCalendarData"
                            >
                              <v-icon start size="small"
                                >mdi-calendar-sync</v-icon
                              >
                              3 Monate laden
                            </v-btn>

                            <v-progress-circular
                              v-else
                              indeterminate
                              size="20"
                              width="2"
                              color="success"
                            ></v-progress-circular>
                          </div>

                          <div class="absence-content">
                            <!-- Abwesenheiten je Monat -->
                            <div class="absence-summary">
                              <div
                                v-for="(month, index) in calendarMonths"
                                :key="index"
                                class="absence-month"
                              >
                                <div class="month-header">
                                  <span class="month-title">{{
                                    month.label
                                  }}</span>
                                </div>
                                <div class="month-absences">
                                  <div class="absence-type compact">
                                    <div class="absence-type-label">
                                      <v-icon
                                        color="amber"
                                        size="small"
                                        class="mr-1"
                                        >mdi-beach</v-icon
                                      >
                                      Urlaubstage
                                    </div>
                                    <div class="absence-type-value">
                                      {{ month.vacationDays }}
                                      <v-chip
                                        v-if="
                                          month.autoLoaded &&
                                          month.vacationDays > 0
                                        "
                                        size="x-small"
                                        color="success"
                                        variant="outlined"
                                        class="absence-badge"
                                        >Auto</v-chip
                                      >
                                    </div>
                                  </div>
                                  <div class="absence-type compact">
                                    <div class="absence-type-label">
                                      <v-icon
                                        color="error"
                                        size="small"
                                        class="mr-1"
                                        >mdi-pill</v-icon
                                      >
                                      Krankheitstage
                                    </div>
                                    <div class="absence-type-value">
                                      {{ month.sickDays }}
                                      <v-chip
                                        v-if="
                                          month.autoLoaded && month.sickDays > 0
                                        "
                                        size="x-small"
                                        color="error"
                                        variant="outlined"
                                        class="absence-badge"
                                        >Auto</v-chip
                                      >
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" md="6">
                        <!-- Rechte Spalte: Berechnungsdetails -->
                        <div class="calculation-details">
                          <div class="details-header">
                            <v-icon color="info" size="small" class="mr-2"
                              >mdi-clipboard-list-outline</v-icon
                            >
                            3-Monats-Arbeitszeit-Übersicht
                          </div>

                          <div class="workdays-info">
                            <!-- Überblick aller drei Monate -->
                            <div class="info-item highlighted">
                              <span class="info-label">Zeitraum:</span>
                              <span class="info-value">{{
                                threeMonthPeriod
                              }}</span>
                            </div>

                            <!-- Gesamt-Übersicht -->
                            <div class="info-section-header">
                              Gesamtübersicht (3 Monate)
                            </div>
                            <div class="info-item">
                              <span class="info-label"
                                >Arbeitstage gesamt:</span
                              >
                              <span class="info-value">{{
                                totalWorkdays
                              }}</span>
                            </div>
                            <div class="info-item with-badge">
                              <span class="info-label"
                                >Urlaubstage gesamt:</span
                              >
                              <span class="info-value">{{
                                totalVacationDays
                              }}</span>
                            </div>
                            <div class="info-item with-badge">
                              <span class="info-label"
                                >Krankheitstage gesamt:</span
                              >
                              <span class="info-value">{{
                                totalSickDays
                              }}</span>
                            </div>
                            <div class="info-item highlighted">
                              <span class="info-label"
                                >Nettoarbeitstage gesamt:</span
                              >
                              <span class="info-value">{{
                                totalNetWorkdays
                              }}</span>
                            </div>
                            <div class="info-item">
                              <span class="info-label"
                                >Nettoarbeitsstunden gesamt:</span
                              >
                              <span class="info-value"
                                >{{
                                  totalNetWorkingHours.toFixed(1)
                                }}
                                Std.</span
                              >
                            </div>
                            <div class="info-item">
                              <span class="info-label"
                                >Nebentätigkeiten gesamt:</span
                              >
                              <span class="info-value"
                                >{{
                                  totalSideActivitiesHours.toFixed(1)
                                }}
                                Std.</span
                              >
                            </div>
                            <div class="info-item highlighted">
                              <span class="info-label"
                                >Verfügbare Stunden gesamt:</span
                              >
                              <span class="info-value"
                                >{{
                                  (
                                    totalNetWorkingHours -
                                    totalSideActivitiesHours
                                  ).toFixed(1)
                                }}
                                Std.</span
                              >
                            </div>

                            <!-- Verteilung der Arbeitsleistung -->
                            <div class="info-section-header mt-3">
                              Tätigkeitsstatistik
                            </div>
                            <div class="activity-distribution">
                              <div
                                v-for="(month, index) in activityMonths"
                                :key="index"
                                class="activity-month"
                              >
                                <div class="month-label">
                                  {{ month.label }}:
                                </div>
                                <div class="month-activities">
                                  <div class="activity-count">
                                    <span class="count-label"
                                      >Tätigkeiten:</span
                                    >
                                    <span class="count-value">{{
                                      month.activityCount || 0
                                    }}</span>
                                  </div>
                                  <div class="activity-hours">
                                    <span class="hours-label">Stunden:</span>
                                    <span class="hours-value">{{
                                      month.activityHours
                                        ? month.activityHours.toFixed(1)
                                        : "0.0"
                                    }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Stundensatzberechnung basierend auf 3-Monats-Daten -->
                            <div class="info-item highlight mt-3">
                              <span class="info-label">
                                Berechneter 3-Monats-Stundensatz:
                                <v-tooltip location="top">
                                  <template v-slot:activator="{ props }">
                                    <v-icon
                                      v-bind="props"
                                      size="small"
                                      class="ml-1"
                                      color="grey"
                                      >mdi-information-outline</v-icon
                                    >
                                  </template>
                                  <div
                                    class="tooltip-content calculation-tooltip"
                                  >
                                    <h4>Berechnungsschritte über 3 Monate:</h4>
                                    <ol>
                                      <li>
                                        Nettoarbeitszeit gesamt:
                                        {{
                                          totalNetWorkingHours.toFixed(1)
                                        }}
                                        Std.
                                      </li>
                                      <li>
                                        Abzug Nebentätigkeiten gesamt:
                                        {{
                                          totalSideActivitiesHours.toFixed(1)
                                        }}
                                        Std.
                                      </li>
                                      <li>
                                        Verfügbare Produktivzeit:
                                        {{
                                          (
                                            totalNetWorkingHours -
                                            totalSideActivitiesHours
                                          ).toFixed(1)
                                        }}
                                        Std.
                                      </li>
                                      <li>
                                        3-Monats-Durchschnittsverdienst:
                                        {{ averageEarnings.toFixed(2) }}€
                                      </li>
                                      <li class="final-formula">
                                        Stundensatz =
                                        {{ averageEarnings.toFixed(2) }}€ ÷
                                        {{
                                          (
                                            totalNetWorkingHours -
                                            totalSideActivitiesHours
                                          ).toFixed(1)
                                        }}
                                        Std. =
                                        {{
                                          threeMonthHourlyRate.toFixed(2)
                                        }}€/Std.
                                      </li>
                                    </ol>
                                  </div>
                                </v-tooltip>
                              </span>
                              <span class="info-value"
                                >{{ threeMonthHourlyRate.toFixed(2) }}€</span
                              >
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Nebentätigkeiten mit neuem Stundensatz -->
                  <div class="earnings-item side-activities mt-4">
                    <div class="earnings-label">
                      <v-icon color="amber" size="small" class="mr-1"
                        >mdi-clock-outline</v-icon
                      >
                      Nebentätigkeiten ({{
                        totalSideActivitiesHours.toFixed(2)
                      }}
                      Std.):
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            v-bind="props"
                            size="small"
                            class="ml-1"
                            color="grey"
                            >mdi-information-outline</v-icon
                          >
                        </template>
                        <div class="tooltip-content">
                          <div>
                            3-Monats-Stundensatz:
                            {{ threeMonthHourlyRate.toFixed(2) }}€
                          </div>
                          <div>
                            Berechnung:
                            {{ totalSideActivitiesHours.toFixed(2) }} Std. ×
                            {{ threeMonthHourlyRate.toFixed(2) }}€
                          </div>
                          <div class="tooltip-note">
                            Basierend auf 3-Monats-Daten
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                    <div class="earnings-value side-value">
                      {{
                        (
                          totalSideActivitiesHours * threeMonthHourlyRate
                        ).toFixed(2)
                      }}€
                    </div>
                  </div>

                  <div class="earnings-total">
  <div class="total-label">
    <v-icon color="success" class="mr-1">mdi-cash</v-icon>
    Gesamt mit Nebentätigkeiten (aktueller Monat):
    <v-tooltip location="top">
      <template v-slot:activator="{ props }">
        <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
      </template>
      <div class="tooltip-content">
        <div>Aktueller Monatsverdienst: {{ overallEarnings.total.toFixed(2) }}€</div>
        <div>Nebentätigkeiten: {{ (totalSideActivitiesHours * threeMonthHourlyRate).toFixed(2) }}€</div>
        <div class="tooltip-note">
          Basierend auf aktuellem Monat + Nebentätigkeiten-Stundensatz aus 3-Monats-Berechnung
        </div>
      </div>
    </v-tooltip>
  </div>
  <div class="total-value grand-total">
    {{
      (
        overallEarnings.total +
        totalSideActivitiesHours * threeMonthHourlyRate
      ).toFixed(2)
    }}€
  </div>
</div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Keine Daten Message -->
            <v-card v-if="!hasCalculationData" class="no-data-card mt-4">
              <v-card-text class="no-data-content">
                <v-icon size="48" color="grey" class="mb-3"
                  >mdi-calculator-off</v-icon
                >
                <h3>Keine Berechnungsdaten verfügbar</h3>
                <p>
                  Für den ausgewählten Zeitraum wurden keine Preisdaten
                  gefunden.
                </p>
              </v-card-text>
            </v-card>
          </v-card-text>

          <!-- Loading Skeleton -->
          <v-card-text v-else class="result-content">
            <v-skeleton-loader
              type="article, list-item-avatar-three-line@2"
            ></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </div>
    </v-expand-transition>

    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <v-progress-circular
        indeterminate
        color="success"
        size="64"
      ></v-progress-circular>
      <span class="loading-text">Daten werden geladen...</span>
    </div>

    <!-- Edit Nebentätigkeit Dialog -->
    <v-dialog v-model="editNebentaetigkeitDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="blue" class="mr-2">mdi-pencil</v-icon>
          Nebentätigkeit bearbeiten
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="editingNebentaetigkeit" class="pt-4">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editingNebentaetigkeit.kommentar"
                label="Kommentar"
                variant="outlined"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingNebentaetigkeit.startzeit"
                label="Startzeit"
                type="datetime-local"
                variant="outlined"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingNebentaetigkeit.endzeit"
                label="Endzeit"
                type="datetime-local"
                variant="outlined"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="editNebentaetigkeitDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="blue"
            variant="elevated"
            @click="updateNebentaetigkeit"
          >
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Nebentätigkeit Dialog -->
    <v-dialog v-model="deleteNebentaetigkeitDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Nebentätigkeit löschen
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          Möchten Sie diese Nebentätigkeit wirklich löschen?
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="deleteNebentaetigkeitDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="red"
            variant="elevated"
            @click="deleteNebentaetigkeit"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PDF-Vorschau (statt neuem Browser-Tab) -->
    <v-dialog
      v-model="pdfPreviewDialog"
      fullscreen
      transition="dialog-transition"
      scrim="rgba(0, 0, 0, 0.72)"
      content-class="pdf-preview-overlay"
      @after-leave="revokePdfPreviewUrl"
    >
      <div class="pdf-preview-shell">
        <v-card class="pdf-preview-card">
          <div class="pdf-preview-header">
            <div class="pdf-preview-heading">
              <div class="pdf-preview-icon">
                <v-icon color="success" size="22">mdi-file-pdf-box</v-icon>
              </div>
              <div>
                <h3 class="pdf-preview-title">PDF-Vorschau</h3>
                <p class="pdf-preview-subtitle">{{ pdfPreviewFileName }}</p>
              </div>
            </div>
            <div class="pdf-preview-actions">
              <v-btn
                variant="outlined"
                color="success"
                prepend-icon="mdi-download"
                class="pdf-action-btn"
                :disabled="!pdfPreviewUrl"
                @click="downloadPdfPreview"
              >
                Speichern
              </v-btn>
              <v-btn
                color="success"
                variant="elevated"
                prepend-icon="mdi-printer"
                class="pdf-action-btn"
                :disabled="!pdfPreviewUrl"
                @click="printPdfPreview"
              >
                Drucken
              </v-btn>
              <v-btn
                icon
                variant="text"
                class="pdf-close-btn"
                aria-label="Schließen"
                @click="closePdfPreview"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <div class="pdf-preview-body">
            <div class="pdf-preview-stage">
              <iframe
                v-if="pdfPreviewUrl"
                ref="pdfPreviewFrame"
                :src="pdfPreviewUrl"
                class="pdf-preview-frame"
                title="PDF-Vorschau"
              ></iframe>
              <div v-else class="pdf-preview-empty">
                <v-icon size="40" class="mb-2">mdi-file-pdf-box</v-icon>
                <span>Keine Vorschau verfügbar</span>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </v-dialog>
  </div>
</v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useToast } from "vue-toastification";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();
const pdfContent = ref(null);
const loading = ref(false);
const pdfPreviewDialog = ref(false);
const pdfPreviewUrl = ref("");
const pdfPreviewFrame = ref(null);
const pdfPreviewFileName = ref("Auswertung.pdf");
const historyData = ref([]);
const loadingHistory = ref(false);

// 3-Monats-Berechnungsdaten
const calendarMonths = ref([]);
const activityMonths = ref([]);
const loadingThreeMonthData = ref(false);

// Nebentätigkeiten-Verwaltung
const detailedNebentaetigkeiten = ref([]);
const expandedNebentaetigkeiten = ref(new Set());
const editNebentaetigkeitDialog = ref(false);
const deleteNebentaetigkeitDialog = ref(false);
const editingNebentaetigkeit = ref(null);
const deletingNebentaetigkeitId = ref(null);
const loadingNebentaetigkeiten = ref(false);

// Berechnet den Text für den 3-Monats-Zeitraum
const threeMonthPeriod = computed(() => {
  if (calendarMonths.value.length === 0) return "Noch keine Daten geladen";

  const monthNames = calendarMonths.value.map((month) => month.label);
  return monthNames.join(", ");
});

// Summen für den 3-Monats-Zeitraum
const totalWorkdays = computed(() => {
  return calendarMonths.value.reduce((sum, month) => sum + month.workdays, 0);
});

const totalVacationDays = computed(() => {
  return calendarMonths.value.reduce(
    (sum, month) => sum + month.vacationDays,
    0
  );
});

const totalSickDays = computed(() => {
  return calendarMonths.value.reduce((sum, month) => sum + month.sickDays, 0);
});

const totalNetWorkdays = computed(() => {
  return totalWorkdays.value - totalVacationDays.value - totalSickDays.value;
});

const totalNetWorkingHours = computed(() => {
  return calendarMonths.value.reduce(
    (sum, month) => sum + month.netWorkingHours,
    0
  );
});

const totalSideActivitiesHours = computed(() => {
  return activityMonths.value.reduce(
    (sum, month) => sum + (month.activityHours || 0),
    0
  );
});

// Summiert den Gesamtverdienst über 3 Monate
const threeMonthTotalEarnings = computed(() => {
  // Wenn keine Daten in historyData, dann 3-Monats-Durchschnitt * 3 verwenden
  if (historyData.value.length === 0) {
    return averageEarnings.value * 3; // Sicherstellen, dass wir den 3-Monats-Wert verwenden
  }

  // Sonst die Summe aller geladenen Monate
  return historyData.value.reduce((sum, month) => sum + month.total, 0);
});

// 3-Monats Stundensatz
const threeMonthHourlyRate = computed(() => {
  if (
    totalNetWorkingHours.value <= totalSideActivitiesHours.value ||
    totalNetWorkingHours.value === 0 ||
    averageEarnings.value === 0
  ) {
    return 0;
  }

  // Durchschnittseinkommen statt Gesamteinkommen verwenden
  return (
    averageEarnings.value /
    (totalNetWorkingHours.value - totalSideActivitiesHours.value)
  );
});

// Lädt Kalender- und Aktivitätsdaten für die letzten 3 Monate
async function loadThreeMonthCalendarData() {
  if (!authStore.user || !selectedMonth.value || !selectedYear.value) {
    toast.error("Keine Auswahl möglich");
    return;
  }

  loadingCalendarData.value = true;
  calendarMonths.value = [];
  activityMonths.value = [];

  try {
    // Aktuelle und die zwei vorherigen Monate bestimmen
    const months = [];
    for (let i = 0; i < 3; i++) {
      let monthIndex = selectedMonth.value.value - i;
      let year = selectedYear.value;

      if (monthIndex <= 0) {
        monthIndex += 12;
        year -= 1;
      }

      const monthName =
        monthOptions.find((m) => m.value === monthIndex)?.label || "";
      months.push({
        monthIndex,
        year,
        label: `${monthName} ${year}`,
      });
    }

    // Für jeden Monat Kalenderdaten laden
    for (const month of months) {
      // Arbeitstage im Monat berechnen
      const daysInMonth = new Date(month.year, month.monthIndex, 0).getDate();
      let workdays = 0;

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(month.year, month.monthIndex - 1, day);
        const dayOfWeek = date.getDay(); // 0=So, 6=Sa
        if (dayOfWeek > 0 && dayOfWeek < 6) {
          workdays++;
        }
      }

      // Kalenderdaten laden
      const response = await axios.get(`${API_BASE_URL}calendar.php`, {
        params: {
          month: month.monthIndex,
          year: month.year,
        },
        withCredentials: true,
      });

      if (response.data.status === "success") {
        // Urlaubs- und Krankheitstage zählen
        const userEvents = response.data.events.filter(
          (event) => event.user_id === authStore.user.id
        );

        let vacationDays = 0;
        let sickDays = 0;
        const countedDays = new Map();

        userEvents.forEach((event) => {
          // Datumsbereich verarbeiten
          const startDateStr = event.start_date.split(" ")[0];
          const endDateStr = event.end_date.split(" ")[0];

          const startParts = startDateStr.split("-").map(Number);
          const endParts = endDateStr.split("-").map(Number);

          const startDate = new Date(
            startParts[0],
            startParts[1] - 1,
            startParts[2]
          );
          const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);

          const formatDateToYMD = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
          };

          const currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            const dateKey = formatDateToYMD(currentDate);

            // Auf Wochenenden und richtigen Monat prüfen
            const dayOfWeek = currentDate.getDay();
            if (
              dayOfWeek !== 0 &&
              dayOfWeek !== 6 &&
              currentDate.getMonth() + 1 === month.monthIndex &&
              currentDate.getFullYear() === month.year
            ) {
              if (!countedDays.has(dateKey)) {
                if (event.event_type === "vacation") {
                  vacationDays++;
                  countedDays.set(dateKey, "vacation");
                } else if (event.event_type === "sick") {
                  sickDays++;
                  countedDays.set(dateKey, "sick");
                }
              }
            }

            currentDate.setDate(currentDate.getDate() + 1);
          }
        });

        // Nettoarbeitsstunden berechnen mit benutzerdefinierten Einstellungen
        const netWorkdays = Math.max(0, workdays - vacationDays - sickDays);
        const netWorkingHours = netWorkdays * hoursPerWorkday.value;

        // Kalenderdaten speichern
        calendarMonths.value.push({
          ...month,
          workdays,
          vacationDays,
          sickDays,
          netWorkdays,
          netWorkingHours,
          autoLoaded: true,
        });

        // Tätigkeitsdaten laden
        try {
          const activityResponse = await axios.post(
            `${API_BASE_URL}summary.php`,
            {
              user_id: authStore.user.id,
              month: month.monthIndex,
              year: month.year,
            },
            {
              withCredentials: true
            }
          );

          if (activityResponse.data.status === "success") {
            const activities = activityResponse.data.nebentaetigkeiten || [];
            const activityCount = activities.length;

            // Gesamtstunden der Nebentätigkeiten berechnen
            const activityMinutes = activities.reduce(
              (sum, item) => sum + Number(item.dauer),
              0
            );
            const activityHours = activityMinutes / 60;

            activityMonths.value.push({
              ...month,
              activityCount,
              activityHours,
            });
          }
        } catch (error) {
          console.error(
            `Fehler beim Laden der Tätigkeitsdaten für ${month.label}:`,
            error
          );
          activityMonths.value.push({
            ...month,
            activityCount: 0,
            activityHours: 0,
            error: true,
          });
        }
      }
    }

    // Nach Datum sortieren (neueste zuerst)
    calendarMonths.value.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.monthIndex - a.monthIndex;
    });

    activityMonths.value.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.monthIndex - a.monthIndex;
    });

    toast.success("3-Monats-Daten erfolgreich geladen");
  } catch (error) {
    console.error("Fehler beim Laden der 3-Monats-Daten:", error);
    toast.error("Fehler beim Laden der 3-Monats-Daten");
  } finally {
    loadingCalendarData.value = false;
  }
}

// ======== Tab-Navigation ========
const activeTab = ref("zeitraum");

// ======== Filter-Zustände ========
// Optionen für den Monat als Objekte
const monthOptions = [
  { value: 1, label: "Januar" },
  { value: 2, label: "Februar" },
  { value: 3, label: "März" },
  { value: 4, label: "April" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juni" },
  { value: 7, label: "Juli" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "Oktober" },
  { value: 11, label: "November" },
  { value: 12, label: "Dezember" },
];

// Liste der letzten 10 Jahre
const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - i
);

// Standardwerte: Aktueller Monat und Jahr
const initialMonth = new Date().getMonth() + 1;
const selectedMonth = ref(monthOptions.find((m) => m.value === initialMonth));
const selectedYear = ref(new Date().getFullYear());

// ======== Daten-Zustand ========
const zusammenfassungen = ref(null);
const preise = ref([]);
const deviceTypes = ref([]);

// ======== Berechnete Eigenschaften ========
// Aktueller Monatsname
const currentMonthLabel = computed(() =>
  selectedMonth.value ? selectedMonth.value.label : ""
);

// Benutzername
const currentUserName = computed(() => {
  if (authStore.user) {
    return authStore.user.vorname && authStore.user.nachname
      ? `${authStore.user.vorname} ${authStore.user.nachname}`
      : authStore.user.loginname;
  }
  return "";
});

// Gruppierung der Instandsetzungen nach Gerätetyp
const groupedInstandsetzungen = computed(() => {
  if (!zusammenfassungen.value || !zusammenfassungen.value.instandsetzungen)
    return {};

  return zusammenfassungen.value.instandsetzungen.reduce((acc, item) => {
    const key = item.geraetetyp || "Unbekannt";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
});

// Gruppierung der Retouren nach Gerätetyp
const groupedRetouren = computed(() => {
  if (!zusammenfassungen.value || !zusammenfassungen.value.retouren) return {};

  return zusammenfassungen.value.retouren.reduce((acc, item) => {
    const key = item.geraetetyp || "Unbekannt";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
});

// Liste aller Gerätetypen für die Anzeige
const deviceTypesDisplay = computed(() =>
  deviceTypes.value.map((item) => item.name)
);

// Gerätetypen, die Daten haben, für die Anzeige
const displayedDeviceTypes = computed(() => {
  return deviceTypesDisplay.value.filter((name) => {
    const inst = groupedInstandsetzungen.value[name];
    const ret = groupedRetouren.value[name];
    return (inst && inst.length > 0) || (ret && ret.length > 0);
  });
});

// Berechnet Kategoriezusammenfassungen pro Gerätetyp
const deviceTypeCategorySummary = computed(() => {
  const summary = {};

  displayedDeviceTypes.value.forEach((geraetetyp) => {
    const categories = {
      'Reparatur': 0,
      'Austausch': 0,
      'Kostenvoranschlag': 0,
      'Unrepariert': 0,
      'A-Ware': 0,
      'B-Ware': 0,
      'Schrott': 0,
      'Retouren Sorting': 0
    };

    // Instandsetzungen zusammenfassen
    const instandItems = groupedInstandsetzungen.value[geraetetyp] || [];
    instandItems.forEach(item => {
      if (item.reparaturtyp in categories) {
        categories[item.reparaturtyp] += parseInt(item.stueckzahl) || 0;
      }
    });

    // Retouren zusammenfassen
    const retourenItems = groupedRetouren.value[geraetetyp] || [];
    retourenItems.forEach(item => {
      if (item.retourentyp in categories) {
        categories[item.retourentyp] += parseInt(item.stueckzahl) || 0;
      }
    });

    // Gesamtzahl berechnen
    const total = Object.values(categories).reduce((sum, val) => sum + val, 0);

    // Nur Kategorien mit Werten behalten
    const filteredCategories = Object.entries(categories)
      .filter(([, count]) => count > 0)
      .map(([name, count]) => ({ name, count }));

    summary[geraetetyp] = {
      categories: filteredCategories,
      total
    };
  });

  return summary;
});

// Gesamtsumme über alle Gerätetypen
const overallCategoryTotal = computed(() => {
  let total = 0;
  Object.values(deviceTypeCategorySummary.value).forEach(summary => {
    total += summary.total;
  });
  return total;
});

// Nur Gerätetypen mit Preiseinträgen für die Berechnungsansicht
const displayedDeviceTypesWithPrice = computed(() => {
  return deviceTypesDisplay.value.filter((name) => {
    const instWithPrice = groupedInstandsetzungen.value[name]
      ? groupedInstandsetzungen.value[name].filter((item) =>
          getPreis(name, item.reparaturtyp)
        )
      : [];
    const retWithPrice = groupedRetouren.value[name]
      ? groupedRetouren.value[name].filter((item) =>
          getPreis(name, item.retourentyp)
        )
      : [];
    return instWithPrice.length > 0 || retWithPrice.length > 0;
  });
});

// Prüft, ob es überhaupt Daten gibt
const hasData = computed(() => {
  const hasInstandsetzungen =
    zusammenfassungen.value &&
    zusammenfassungen.value.instandsetzungen &&
    zusammenfassungen.value.instandsetzungen.length > 0;

  const hasRetouren =
    zusammenfassungen.value &&
    zusammenfassungen.value.retouren &&
    zusammenfassungen.value.retouren.length > 0;

  const hasNebentaetigkeiten =
    zusammenfassungen.value &&
    zusammenfassungen.value.nebentaetigkeiten &&
    zusammenfassungen.value.nebentaetigkeiten.length > 0;

  return hasInstandsetzungen || hasRetouren || hasNebentaetigkeiten;
});

// Prüft, ob es Berechnungsdaten gibt
const hasCalculationData = computed(() => {
  return (
    Object.keys(deviceTypeEarnings.value).length > 0 &&
    overallEarnings.value.total > 0
  );
});

// ======== Zeit-Formatierung ========
// Formatierung einer Dauer in Minuten zu lesebarem Format
const formatDuration = (minutes) => {
  if (isNaN(minutes) || minutes === null) return "0 Min.";

  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return hours > 0 ? `${hours} Std. ${mins} Min.` : `${mins} Min.`;
};

// Nebentätigkeiten: Gesamtdauer (kurze Version)
const totalDurationShort = computed(() => {
  if (
    !zusammenfassungen.value?.nebentaetigkeiten ||
    zusammenfassungen.value.nebentaetigkeiten.length === 0
  ) {
    return "0 Min.";
  }

  const totalMinutes = zusammenfassungen.value.nebentaetigkeiten.reduce(
    (sum, item) => sum + Number(item.dauer),
    0
  );

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} Std. ${minutes} Min. (${totalMinutes} Min.)`;
});

// Nebentätigkeiten: Gesamtdauer (detaillierte Version) - Aktualisiert mit dynamischer Arbeitszeit
const totalDurationDetailed = computed(() => {
  if (
    !zusammenfassungen.value?.nebentaetigkeiten ||
    zusammenfassungen.value.nebentaetigkeiten.length === 0
  ) {
    return "0 Std. / 0 Tage";
  }

  const totalMinutes = zusammenfassungen.value.nebentaetigkeiten.reduce(
    (sum, item) => sum + Number(item.dauer),
    0
  );

  const hours = (totalMinutes / 60).toFixed(2);
  // Dynamische Berechnung des Stundensatzes basierend auf Benutzereinstellungen
  const days = (totalMinutes / 60 / hoursPerWorkday.value).toFixed(2);
  return `${hours} dez. Std. / ${days} Tage`;
});

// ======== Preis-Berechnungen ========
// Hilfsfunktion: Preis zu Gerätetyp und Kategorie finden
const getPreis = (deviceType, kategorie) => {
  if (!kategorie || !deviceType) return null;

  return preise.value.find(
    (p) => p.geraetetyp === deviceType && p.kategorie === kategorie
  );
};

// Berechnung des Verdienstes pro Gerätetyp
const deviceTypeEarnings = computed(() => {
  const earnings = {};

  for (const type of displayedDeviceTypes.value) {
    let instandEarnings = 0;
    let retourEarnings = 0;

    // Berechnung für Instandsetzungen
    const instandList = groupedInstandsetzungen.value[type] || [];
    for (const item of instandList) {
      const preisEntry = getPreis(type, item.reparaturtyp);
      if (preisEntry) {
        instandEarnings += item.stueckzahl * parseFloat(preisEntry.preis);
      }
    }

    // Berechnung für Retouren
    const retourList = groupedRetouren.value[type] || [];
    for (const item of retourList) {
      const preisEntry = getPreis(type, item.retourentyp);
      if (preisEntry) {
        retourEarnings += item.stueckzahl * parseFloat(preisEntry.preis);
      }
    }

    // Nur Gerätetypen mit Verdienst speichern
    if (instandEarnings > 0 || retourEarnings > 0) {
      earnings[type] = {
        instand: instandEarnings,
        retour: retourEarnings,
        total: instandEarnings + retourEarnings,
      };
    }
  }

  return earnings;
});

// Gesamtverdienst über alle Gerätetypen
const overallEarnings = computed(() => {
  let instandTotal = 0;
  let retourTotal = 0;

  Object.keys(deviceTypeEarnings.value).forEach((type) => {
    instandTotal += deviceTypeEarnings.value[type].instand;
    retourTotal += deviceTypeEarnings.value[type].retour;
  });

  return {
    instand: instandTotal,
    retour: retourTotal,
    total: instandTotal + retourTotal,
  };
});

// ======== API-Interaktionen ========
// Zusammenfassungsdaten laden
const fetchZusammenfassungen = async () => {
  loading.value = true;

  try {
    const response = await axios.post(`${API_BASE_URL}summary.php`, {
      user_id: authStore.user.id,
      month: selectedMonth.value.value,
      year: selectedYear.value,
    }, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      zusammenfassungen.value = response.data;
    } else {
      console.error(
        "Fehler beim Abrufen der Zusammenfassungen:",
        response.data.message
      );
      toast.error("Fehler beim Abrufen der Zusammenfassungen");
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Zusammenfassungen:", error);
    toast.error("Verbindungsfehler beim Laden der Daten");
  } finally {
    loading.value = false;
  }
};

// Preisdaten laden
const fetchPreise = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/prices.php?action=get`, {
      withCredentials: true
    });
    if (response.data.status === "success") {
      preise.value = response.data.preise;
    } else {
      console.error("Fehler beim Abrufen der Preise:", response.data.message);
      toast.error("Fehler beim Abrufen der Preise");
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Preise:", error);
    toast.error("Verbindungsfehler beim Laden der Preisdaten");
  }
};

// Benutzerdaten laden/aktualisieren
const fetchUser = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "get",
        user_id: authStore.user.id,
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" }
      }
    );

    if (
      response.data.status === "success" &&
      response.data.users &&
      response.data.users.length > 0
    ) {
      // Suche den aktuell angemeldeten User anhand der ID oder verwende den ersten Eintrag als Fallback
      const currentUserData =
        response.data.users.find((u) => u.id === authStore.user.id) ||
        response.data.users[0];
      authStore.user = { ...authStore.user, ...currentUserData };
    } else {
      console.error("Fehler beim Abrufen des Users:", response.data.message);
      toast.error("Fehler beim Abrufen der Benutzerdaten");
    }
  } catch (error) {
    console.error("Fehler beim Abrufen des Users:", error);
    toast.error("Verbindungsfehler beim Laden der Benutzerdaten");
  }
};

// ======== Arbeitszeit-Einstellungen ========
const userWorkTimeSettings = ref({
  weeklyHours: 38.5,    // Standardwert: 38,5 Stunden pro Woche
  workdaysPerWeek: 5,   // Standardwert: 5 Tage pro Woche
  lastUpdated: null
});

// Berechnete Eigenschaften basierend auf den Arbeitszeit-Einstellungen
const hoursPerWorkday = computed(() => {
  if (!userWorkTimeSettings.value.workdaysPerWeek || userWorkTimeSettings.value.workdaysPerWeek === 0) return 0;
  return userWorkTimeSettings.value.weeklyHours / userWorkTimeSettings.value.workdaysPerWeek;
});

// Funktion zum Abrufen der benutzerdefinierten Arbeitszeit-Einstellungen
const fetchUserWorkTimeSettings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}user_worktime_settings.php`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      userWorkTimeSettings.value = {
        ...userWorkTimeSettings.value, // Standardwerte beibehalten
        ...response.data.settings,     // Mit Server-Daten überschreiben
      };
    }
  } catch (error) {
    console.error("❌ Fehler beim Laden der Arbeitszeit-Einstellungen:", error);
    // Keine Fehlermeldung anzeigen - wir verwenden einfach die Standardwerte
  }
};

// Gerätetypen laden
const fetchDeviceTypes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      deviceTypes.value = response.data.geraetetypen;
    } else {
      console.error(
        "Fehler beim Abrufen der Gerätetypen:",
        response.data.message
      );
      toast.error("Fehler beim Abrufen der Gerätetypen");
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Gerätetypen:", error);
    toast.error("Verbindungsfehler beim Laden der Gerätekategorien");
  }
};

// ======== Nebentätigkeiten-Verwaltung ========
// Detaillierte Nebentätigkeiten laden
const fetchDetailedNebentaetigkeiten = async () => {
  if (!authStore.user || !selectedMonth.value || !selectedYear.value) return;

  loadingNebentaetigkeiten.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      { action: "get" },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      // Nur Einträge für den ausgewählten Monat filtern
      detailedNebentaetigkeiten.value = response.data.records.filter((item) => {
        const startzeit = new Date(item.startzeit);
        return (
          startzeit.getMonth() + 1 === selectedMonth.value.value &&
          startzeit.getFullYear() === selectedYear.value
        );
      });
    }
  } catch (error) {
    console.error("Fehler beim Laden der Nebentätigkeiten:", error);
    toast.error("Fehler beim Laden der Nebentätigkeiten");
  } finally {
    loadingNebentaetigkeiten.value = false;
  }
};

// Gruppierte Nebentätigkeiten mit Details
const groupedNebentaetigkeitenWithDetails = computed(() => {
  const grouped = {};

  detailedNebentaetigkeiten.value.forEach((item) => {
    if (!grouped[item.kommentar]) {
      grouped[item.kommentar] = {
        kommentar: item.kommentar,
        totalDauer: 0,
        items: [],
      };
    }

    const startzeit = new Date(item.startzeit);
    const endzeit = new Date(item.endzeit);
    const dauer = Math.floor((endzeit - startzeit) / 60000); // in Minuten

    grouped[item.kommentar].totalDauer += dauer;
    grouped[item.kommentar].items.push({
      ...item,
      dauer,
    });
  });

  return grouped;
});

// Toggle Expansion
const toggleNebentaetigkeit = (kommentar) => {
  if (expandedNebentaetigkeiten.value.has(kommentar)) {
    expandedNebentaetigkeiten.value.delete(kommentar);
  } else {
    expandedNebentaetigkeiten.value.add(kommentar);
  }
};

// Nebentätigkeit bearbeiten öffnen
const openEditNebentaetigkeit = (item) => {
  editingNebentaetigkeit.value = { ...item };
  editNebentaetigkeitDialog.value = true;
};

// Nebentätigkeit aktualisieren
const updateNebentaetigkeit = async () => {
  if (!editingNebentaetigkeit.value) return;

  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      {
        action: "update",
        id: editingNebentaetigkeit.value.id,
        startzeit: editingNebentaetigkeit.value.startzeit,
        endzeit: editingNebentaetigkeit.value.endzeit,
        kommentar: editingNebentaetigkeit.value.kommentar,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      toast.success("Nebentätigkeit erfolgreich aktualisiert!");
      editNebentaetigkeitDialog.value = false;
      await Promise.all([fetchDetailedNebentaetigkeiten(), fetchZusammenfassungen()]);
    } else {
      toast.error(response.data?.message || "Fehler beim Aktualisieren!");
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
    toast.error("Verbindungsfehler beim Aktualisieren.");
  }
};

// Nebentätigkeit löschen Dialog öffnen
const openDeleteNebentaetigkeit = (id) => {
  deletingNebentaetigkeitId.value = id;
  deleteNebentaetigkeitDialog.value = true;
};

// Nebentätigkeit löschen
const deleteNebentaetigkeit = async () => {
  if (!deletingNebentaetigkeitId.value) return;

  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      {
        action: "delete",
        id: deletingNebentaetigkeitId.value,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      toast.success("Nebentätigkeit erfolgreich gelöscht!");
      deleteNebentaetigkeitDialog.value = false;
      await Promise.all([fetchDetailedNebentaetigkeiten(), fetchZusammenfassungen()]);
    } else {
      toast.error(response.data?.message || "Fehler beim Löschen!");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Verbindungsfehler beim Löschen.");
  }
};

// ======== PDF-Export ========
const exportPdf = async () => {
  if (!zusammenfassungen.value) {
    toast.error("Keine Daten zum Exportieren vorhanden");
    return;
  }

  toast.info("PDF wird erstellt...");

  await nextTick();
  const element = pdfContent.value.$el
    ? pdfContent.value.$el
    : pdfContent.value;
  if (!element) {
    toast.error("Element für PDF-Export nicht gefunden");
    return;
  }

  try {
    // Element klonen und für Schwarz/Weiß-Druck optimieren
    const clonedElement = element.cloneNode(true);

    // Druckfreundliches CSS: Struktur und Rahmen beibehalten, nur Glaseffekte entfernen
    const style = document.createElement("style");
    style.textContent = `
      * {
        box-shadow: none !important;
        text-shadow: none !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        animation: none !important;
        transition: none !important;
        transform: none !important;
        color: #000 !important;
        -webkit-text-fill-color: #000 !important;
        fill: #000 !important;
        stroke: #000 !important;
        background: #fff !important;
        background-color: #fff !important;
        background-image: none !important;
      }
      .summary-result-card,
      .device-card,
      .nebentaetigkeiten-card,
      .overall-total-card,
      .no-data-card {
        border: 1px solid #000 !important;
        border-radius: 6px !important;
      }
      .result-header,
      .device-header,
      .activity-header,
      .earnings-header {
        border: none !important;
        border-bottom: 1px solid #000 !important;
      }
      .stat-label {
        border: none !important;
        border-bottom: 1px solid #000 !important;
      }
      .stat-item,
      .summary-total,
      .activity-item,
      .activity-item-detail {
        border: 1px solid #000 !important;
        border-radius: 6px !important;
      }
      .stat-value,
      .overall-total-item,
      .overall-total-label,
      .overall-total-value,
      .title-container,
      .v-card-text {
        border: none !important;
      }
      .v-divider,
      .summary-divider {
        display: none !important;
      }
      .v-icon::before,
      .v-icon::after {
        color: #000 !important;
      }
    `;
    clonedElement.appendChild(style);

    // Funktion zum vollständigen Entfernen aller Farben und Hintergründe
    const convertToBlackAndWhite = (node) => {
      if (!node || !node.style) return;
      if (node.classList.contains("activity-header")) {
        node.style.setProperty("display", "flex", "important");
        node.style.setProperty("justify-content", "flex-start", "important");
        node.style.setProperty("text-align", "left", "important");
      }

      if (node.classList.contains("activity-title")) {
        node.style.setProperty("text-align", "left", "important");
      }

      // Alle Hintergründe und Textfarben für Schwarz/Weiß erzwingen
      node.style.setProperty("background", "#fff", "important");
      node.style.setProperty("backgroundColor", "#fff", "important");
      node.style.setProperty("backgroundImage", "none", "important");
      node.style.setProperty("color", "#000", "important");
      node.style.setProperty("-webkit-text-fill-color", "#000", "important");

      // Nur Druck-störende Effekte entfernen (Layout und Rahmen bleiben erhalten)
      const resetProperties = [
        "backdropFilter",
        "webkitBackdropFilter",
        "boxShadow",
        "textShadow",
        "animation",
        "transition",
        "filter",
        "webkitFilter",
        "transform",
        "borderImage",
      ];

      resetProperties.forEach((prop) => {
        try {
          node.style.setProperty(prop, "none", "important");
        } catch (e) {
          // Ignorieren, falls Eigenschaft nicht unterstützt
        }
      });

      // Besondere Behandlung für verschiedene Elementtypen
      if (node.tagName) {
        if (
          node.classList.contains("v-icon") ||
          node.tagName.toLowerCase() === "i" ||
          node.tagName.toLowerCase() === "svg"
        ) {
          node.style.setProperty("color", "black", "important");
          node.style.setProperty("fill", "black", "important");

          // SVG-spezifische Behandlung
          if (node.tagName.toLowerCase() === "svg") {
            const paths = node.querySelectorAll("path");
            paths.forEach((path) => {
              path.setAttribute("fill", "black");
              path.setAttribute("stroke", "black");
            });
          }
        }

        // Rahmen nur auf äußere Karten und Datenzeilen
        if (
          node.classList.contains("summary-result-card") ||
          node.classList.contains("device-card") ||
          node.classList.contains("nebentaetigkeiten-card") ||
          node.classList.contains("overall-total-card") ||
          node.classList.contains("activity-item") ||
          node.classList.contains("stat-item") ||
          node.classList.contains("summary-total")
        ) {
          node.style.setProperty("border", "1px solid #000", "important");
          node.style.setProperty("borderRadius", "6px", "important");
        }

        if (
          node.classList.contains("stat-value") ||
          node.classList.contains("overall-total-item") ||
          node.classList.contains("overall-total-label") ||
          node.classList.contains("overall-total-value") ||
          node.classList.contains("title-container") ||
          node.classList.contains("v-card-text")
        ) {
          node.style.setProperty("border", "none", "important");
        }

        if (
          node.classList.contains("result-header") ||
          node.classList.contains("device-header") ||
          node.classList.contains("activity-header")
        ) {
          node.style.setProperty("border", "none", "important");
          node.style.setProperty("borderBottom", "1px solid #000", "important");
        }

        // CSS-Klassen entfernen, die Farbeffekte haben könnten
        if (node.classList) {
          const classesToRemove = [
            "primary",
            "secondary",
            "accent",
            "error",
            "info",
            "success",
            "warning",
            "deep-purple",
            "light-blue",
            "amber",
            "red",
            "pink",
            "purple",
            "deep-purple",
            "indigo",
            "blue",
            "light-blue",
            "cyan",
            "teal",
            "green",
            "light-green",
            "lime",
            "yellow",
            "amber",
            "orange",
            "deep-orange",
            "brown",
            "grey",
            "blue-grey",
            "elevation",
            "shadow",
            "rounded",
            "gradient",
          ];

          classesToRemove.forEach((cls) => {
            node.classList.remove(cls);
            node.classList.remove(`${cls}-text`);
            node.classList.remove(`${cls}-background`);
          });
        }
      }

      // Rekursiv für alle Kinder ausführen
      if (node.childNodes && node.childNodes.length > 0) {
        node.childNodes.forEach((child) => {
          convertToBlackAndWhite(child);
        });
      }
    };

    convertToBlackAndWhite(clonedElement);

    // Unnötige Elemente entfernen
    const removeUnnecessaryElements = (node) => {
      // Buttons, Icons und UI-Elemente entfernen, die nicht zum Inhalt gehören
      const selectorsToRemove = [
        "button:not(.pdf-keep)",
        ".v-btn:not(.pdf-keep)",
        ".maximize-btn",
        ".action-btn",
        ".v-avatar",
        ".v-progress-circular",
        ".v-progress-linear",
        ".v-skeleton-loader",
        ".v-slider",
        ".v-switch",
        ".v-divider",
        ".summary-divider",
      ];

      try {
        const elementsToRemove = node.querySelectorAll(
          selectorsToRemove.join(",")
        );
        elementsToRemove.forEach((el) => {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        });
      } catch (e) {
        console.warn("Fehler beim Entfernen unnötiger Elemente:", e);
      }

      // Animationen entfernen
      const animatedElements = node.querySelectorAll(
        '[class*="animated"], [class*="pulse"]'
      );
      animatedElements.forEach((el) => {
        if (el.classList) {
          const classesToRemove = Array.from(el.classList).filter(
            (cls) => cls.includes("animated") || cls.includes("pulse")
          );
          el.classList.remove(...classesToRemove);
        }
      });
    };

    removeUnnecessaryElements(clonedElement);

    // Bereite den Klon für den Screenshot vor
    clonedElement.style.position = "absolute";
    clonedElement.style.top = "-9999px";
    clonedElement.style.width = "1000px"; // Fixe Breite für bessere Kontrolle
    // Weißer Hintergrund für das gesamte Dokument
    clonedElement.style.backgroundColor = "#ffffff";
    document.body.appendChild(clonedElement);

    // Screenshot mit optimierten Einstellungen erstellen
    const canvas = await html2canvas(clonedElement, {
      backgroundColor: "#ffffff", // Garantierter weißer Hintergrund
      scale: 2, // Höhere Qualität
      logging: false,
      useCORS: true,
      removeContainer: true,
      // Hintergrundfarben erzwingen
      onclone: (clonedDoc) => {
        const printStyle = clonedDoc.createElement("style");
        printStyle.textContent = `
          * {
            box-shadow: none !important;
            text-shadow: none !important;
            backdrop-filter: none !important;
            color: #000 !important;
            fill: #000 !important;
            background: #fff !important;
            background-color: #fff !important;
            background-image: none !important;
          }
          .stat-value,
          .overall-total-item,
          .overall-total-label,
          .overall-total-value,
          .title-container,
          .v-card-text {
            border: none !important;
          }
          .overall-total-card,
          .device-card,
          .stat-item,
          .summary-total {
            border: 1px solid #000 !important;
          }
        `;
        clonedDoc.head.appendChild(printStyle);
      },
    });

    // Klon entfernen, wenn er fertig ist
    document.body.removeChild(clonedElement);

    // PDF erzeugen
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    // Seitenmaße und Ränder
    const pdfPageWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pdfPageWidth - margin * 2;
    const contentHeight = pdfPageHeight - margin * 2 - 80;

    // Optimale Größenanpassung für eine Seite
    let imgWidth = contentWidth;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Wenn das Bild zu hoch ist, Breite entsprechend anpassen
    if (imgHeight > contentHeight) {
      imgHeight = contentHeight;
      imgWidth = (canvas.width * imgHeight) / canvas.height;
    }

    // Bild zentrieren
    const xPosition = margin + (contentWidth - imgWidth) / 2;

    // Bild einfügen
    pdf.addImage(imgData, "PNG", xPosition, margin, imgWidth, imgHeight);

    // Datum für Signaturfelder
    const currentDate = new Date();
    const formattedDate =
      ("0" + currentDate.getDate()).slice(-2) +
      "." +
      ("0" + (currentDate.getMonth() + 1)).slice(-2) +
      "." +
      currentDate.getFullYear();

    // Signaturfelder
    // Definition der Signaturfelder
    const signatureStartY = margin + imgHeight + 40; // Abstand zum Bild
    const columnGap = 20;
    const signatureWidth = (pdfPageWidth - margin * 2 - columnGap) / 2;
    const signatureBoxHeight = 60; // Höhe der Boxen
    const leftX = margin;
    const rightX = margin + signatureWidth + columnGap;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.setLineWidth(0.5);

    // Linkes Signaturfeld für Arbeitnehmer
    pdf.roundedRect(
      leftX,
      signatureStartY,
      signatureWidth,
      signatureBoxHeight,
      5,
      5
    );
    pdf.text("Datum: " + formattedDate, leftX + 5, signatureStartY + 15);
    pdf.text(
      "Arbeitnehmer",
      leftX + signatureWidth / 2,
      signatureStartY + signatureBoxHeight - 10,
      { align: "center" }
    );

    // Rechtes Signaturfeld für Vorarbeiter
    pdf.roundedRect(
      rightX,
      signatureStartY,
      signatureWidth,
      signatureBoxHeight,
      5,
      5
    );
    pdf.text("Datum: " + formattedDate, rightX + 5, signatureStartY + 15);
    pdf.text(
      "Vorarbeiter",
      rightX + signatureWidth / 2,
      signatureStartY + signatureBoxHeight - 10,
      { align: "center" }
    );

    const fileName = `Auswertung_${currentMonthLabel.value}_${selectedYear.value}_${authStore.user.mitarbeiternummer}.pdf`;

    // PDF in Modal-Vorschau öffnen (kein neuer Tab)
    revokePdfPreviewUrl();
    const pdfBlob = pdf.output("blob");
    pdfPreviewFileName.value = fileName;
    pdfPreviewUrl.value = URL.createObjectURL(pdfBlob);
    pdfPreviewDialog.value = true;

    toast.success("PDF-Vorschau geöffnet");
  } catch (error) {
    console.error("Fehler beim PDF-Export:", error);
    toast.error("Beim Erstellen der PDF ist ein Fehler aufgetreten");
  }
};

const revokePdfPreviewUrl = () => {
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = "";
  }
};

const closePdfPreview = () => {
  pdfPreviewDialog.value = false;
};

const printPdfPreview = () => {
  const frame = pdfPreviewFrame.value;
  if (!frame) return;
  try {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
  } catch (error) {
    console.error("PDF-Druck fehlgeschlagen:", error);
    toast.error("Drucken nicht möglich – bitte Speichern und lokal öffnen");
  }
};

const downloadPdfPreview = () => {
  if (!pdfPreviewUrl.value) return;
  const link = document.createElement("a");
  link.href = pdfPreviewUrl.value;
  link.download = pdfPreviewFileName.value || "Auswertung.pdf";
  link.click();
};

onBeforeUnmount(() => {
  revokePdfPreviewUrl();
});

// ======== Neue Variablen für die 3-Monats-Durchschnittsberechnung ========
// Berechnet die Namen der 3 Monate für den Durchschnitt (aktueller + 2 vorherige)
const previousMonthsText = computed(() => {
  if (!selectedMonth.value) return "";

  const months = [];

  // Aktueller Monat
  months.push(selectedMonth.value.label);

  // Vorheriger Monat
  let prevMonthIndex = selectedMonth.value.value - 2;
  if (prevMonthIndex < 0) {
    prevMonthIndex += 12;
    months.push(
      monthOptions[prevMonthIndex].label + " (" + (selectedYear.value - 1) + ")"
    );
  } else {
    months.push(monthOptions[prevMonthIndex].label);
  }

  // Vorletzter Monat
  let prevPrevMonthIndex = selectedMonth.value.value - 3;
  if (prevPrevMonthIndex < 0) {
    prevPrevMonthIndex += 12;
    months.push(
      monthOptions[prevPrevMonthIndex].label +
        " (" +
        (selectedYear.value - 1) +
        ")"
    );
  } else {
    months.push(monthOptions[prevPrevMonthIndex].label);
  }

  return months.join(", ");
});

// Berechnet den Durchschnitt der letzten 3 Monate
const averageEarnings = computed(() => {
  if (historyData.value.length === 0) return 0;

  const sum = historyData.value.reduce((acc, month) => acc + month.total, 0);
  return sum / historyData.value.length;
});

// Funktion zum Laden der historischen Verdienstdaten
const fetchHistoricalEarnings = async () => {
  if (!authStore.user || !selectedMonth.value || !selectedYear.value) return;

  loadingHistory.value = true;
  historyData.value = []; // Array leeren

  try {
    // Mapping für bereits abgerufene Monate, um Duplikate zu vermeiden
    const processedMonths = new Map();

    // Aktuelle Monat hinzufügen (bereits in overallEarnings vorhanden)
    const currentMonthKey = `${selectedYear.value}-${selectedMonth.value.value}`;
    processedMonths.set(currentMonthKey, true);

    historyData.value.push({
      month: selectedMonth.value.value,
      year: selectedYear.value,
      label: `${selectedMonth.value.label} ${selectedYear.value}`,
      total: overallEarnings.value.total,
      instand: overallEarnings.value.instand,
      retour: overallEarnings.value.retour,
    });

    // Die zwei vorherigen Monate abrufen
    for (let i = 1; i <= 2; i++) {
      let monthIndex = selectedMonth.value.value - i;
      let year = selectedYear.value;

      // Jahreswechsel berücksichtigen
      if (monthIndex <= 0) {
        monthIndex += 12;
        year -= 1;
      }

      // Prüfen ob dieser Monat bereits verarbeitet wurde
      const monthKey = `${year}-${monthIndex}`;
      if (processedMonths.has(monthKey)) continue;
      processedMonths.set(monthKey, true);

      // Historische Daten abrufen
      const response = await axios.post(`${API_BASE_URL}summary.php`, {
        user_id: authStore.user.id,
        month: monthIndex,
        year: year,
      }, {
        withCredentials: true
      });

      if (response.data.status === "success") {
        // Preis-Berechnungen für diesen historischen Monat
        let instandTotal = 0;
        let retourTotal = 0;

        // Instandsetzungen berechnen
        if (response.data.instandsetzungen) {
          for (const item of response.data.instandsetzungen) {
            const preisEntry = preise.value.find(
              (p) =>
                p.geraetetyp === item.geraetetyp &&
                p.kategorie === item.reparaturtyp
            );
            if (preisEntry) {
              instandTotal += item.stueckzahl * parseFloat(preisEntry.preis);
            }
          }
        }

        // Retouren berechnen
        if (response.data.retouren) {
          for (const item of response.data.retouren) {
            const preisEntry = preise.value.find(
              (p) =>
                p.geraetetyp === item.geraetetyp &&
                p.kategorie === item.retourentyp
            );
            if (preisEntry) {
              retourTotal += item.stueckzahl * parseFloat(preisEntry.preis);
            }
          }
        }

        // Gesamtsumme berechnen und zum Verlauf hinzufügen
        const totalEarnings = instandTotal + retourTotal;
        const monthLabel =
          monthOptions.find((m) => m.value === monthIndex)?.label || "";

        historyData.value.push({
          month: monthIndex,
          year: year,
          label: `${monthLabel} ${year}`,
          total: totalEarnings,
          instand: instandTotal,
          retour: retourTotal,
        });
      }
    }

    // Nach Datum sortieren (neueste zuerst)
    historyData.value.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  } catch (error) {
    console.error("Fehler beim Laden der historischen Daten:", error);
    toast.error("Fehler beim Berechnen des Durchschnitts");
  } finally {
    loadingHistory.value = false;
  }
};

// Verbesserte Watch-Funktion mit Deep-Option, um Duplikate zu vermeiden
watch(
  [selectedMonth, selectedYear],
  () => {
    historyData.value = []; // Historie leeren bei Monatswechsel
  },
  { immediate: true }
);

// Diese Watch-Funktion nur einmal triggern, wenn die Berechnungen abgeschlossen sind
watch(
  () => ({ ...overallEarnings.value }),
  (newVal) => {
    if (newVal.total > 0) {
      // Kleine Verzögerung, um mehrfache Aufrufe zu vermeiden
      setTimeout(() => {
        if (historyData.value.length === 0) {
          fetchHistoricalEarnings();
        }
      }, 100);
    } else {
      historyData.value = [];
    }
  },
  { deep: true }
);

// ======== Neue berechnete Eigenschaften für den dynamischen Nebentätigkeiten-Stundensatz ========
// Berechnet Stundensatz aus 1/3 des 3-Monats-Durchschnitts
const hourlyRateFromAverage = computed(() => {
  return averageEarnings.value / 3; // Stundensatz = 1/3 des 3-Monats-Durchschnitts
});

// Gesamtzeit der Nebentätigkeiten in Stunden
const sideActivitiesHours = computed(() => {
  if (
    !zusammenfassungen.value?.nebentaetigkeiten ||
    zusammenfassungen.value.nebentaetigkeiten.length === 0
  ) {
    return 0;
  }

  const totalMinutes = zusammenfassungen.value.nebentaetigkeiten.reduce(
    (sum, item) => sum + Number(item.dauer),
    0
  );

  return totalMinutes / 60; // Minuten in Stunden umrechnen
});

// ======== Neue Variablen für die Arbeitszeitberechnung ========
const vacationDays = ref(0); // Urlaubstage
const sickDays = ref(0); // Krankentage

// Berechnet die Arbeitstage im ausgewählten Monat (ohne Wochenenden)
const workdaysInMonth = computed(() => {
  if (!selectedYear.value || !selectedMonth.value) return 0;

  const year = selectedYear.value;
  const month = selectedMonth.value.value - 1; // JavaScript Monate sind 0-basiert

  const firstDay = new Date(year, month, 1);

  // Für den aktuellen Monat nur bis zum heutigen Tag zählen
  const today = new Date();
  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();
  const lastDay = isCurrentMonth
    ? new Date(today.getFullYear(), today.getMonth(), today.getDate())
    : new Date(year, month + 1, 0);

  let workdays = 0;

  // Jeden Tag des Monats durchgehen (oder bis heute für den aktuellen Monat)
  for (
    let day = new Date(firstDay);
    day <= lastDay;
    day.setDate(day.getDate() + 1)
  ) {
    // 0 = Sonntag, 6 = Samstag - also 1-5 sind Arbeitstage
    const dayOfWeek = day.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workdays++;
    }
  }

  return workdays;
});

// Nettoarbeitstage = Arbeitstage - Urlaub - Krankheit
const netWorkdays = computed(() => {
  const vacation = parseInt(vacationDays.value) || 0;
  const sick = parseInt(sickDays.value) || 0;
  return Math.max(0, workdaysInMonth.value - vacation - sick);
});

// Aktualisierte netWorkingHours Berechnung (für einzelnen Monat)
const netWorkingHours = computed(() => {
  if (!selectedYear.value || !selectedMonth.value || netWorkdays.value === 0)
    return 0;

  // Verwenden der benutzerdefinierten Stunden pro Tag
  return netWorkdays.value * hoursPerWorkday.value;
});

// Stundensatz basierend auf dem Monatsverdienst und den verfügbaren Arbeitsstunden
const hourlyRate = computed(() => {
  if (
    netWorkingHours.value <= sideActivitiesHours.value ||
    netWorkingHours.value === 0 ||
    averageEarnings.value === 0
  ) {
    return 0;
  }

  // Stundensatz = 3-Monats-Durchschnitt / verbleibende Arbeitsstunden
  return (
    averageEarnings.value / (netWorkingHours.value - sideActivitiesHours.value)
  );
});

// Berechneter Verdienst für Nebentätigkeiten
const sideActivitiesEarnings = computed(() => {
  return sideActivitiesHours.value * hourlyRate.value;
});

// Gesamtverdienst mit Nebentätigkeiten
const totalWithSideActivities = computed(() => {
  const baseTotal = overallEarnings.value.total;
  const sideTotal = hourlyRate.value > 0 ? sideActivitiesEarnings.value : 0;

  return baseTotal + sideTotal;
});

// Neue Variablen zur Unterstützung des Auto-Loading
const autoLoadedVacationDays = ref(0);
const autoLoadedSickDays = ref(0);
const loadingCalendarData = ref(false);

// Funktion zum Laden der Urlaubs- und Krankheitsdaten aus dem Kalender
async function loadCalendarData() {
  if (!authStore.user || !selectedMonth.value || !selectedYear.value) {
    toast.error("Keine Auswahl möglich");
    return;
  }

  loadingCalendarData.value = true;

  try {
    const year = parseInt(selectedYear.value);
    const month = parseInt(selectedMonth.value.value); // 1-12

    // Parameter für die API-Anfrage
    const params = {
      month: month,
      year: year,
    };

    // Kalenderdaten abrufen
    const response = await axios.get(`${API_BASE_URL}calendar.php`, {
      params,
      withCredentials: true,
    });

    if (response.data.status === "success") {
      // Ermitteln der Urlaubs- und Krankheitstage des Benutzers
      const userEvents = response.data.events.filter(
        (event) => event.user_id === authStore.user.id
      );

      // Tage pro Typ zählen
      let vacationDaysCount = 0;
      let sickDaysCount = 0;

      // Map für bereits gezählte Tage
      const countedDays = new Map();

      userEvents.forEach((event) => {
        // Extrahiere das Datum (YYYY-MM-DD) aus den DateTime-Strings
        const startDateStr = event.start_date.split(" ")[0];
        const endDateStr = event.end_date.split(" ")[0];

        // Verarbeite jeden Tag im Zeitraum zwischen Start- und Enddatum
        const startParts = startDateStr.split("-").map(Number);
        const endParts = endDateStr.split("-").map(Number);

        // Erstelle JavaScript Date-Objekte mit der lokalen Zeitzone
        const startDate = new Date(
          startParts[0],
          startParts[1] - 1,
          startParts[2]
        );
        const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);

        // Formatierungsfunktion für konsistente Datumsstrings im Format YYYY-MM-DD
        const formatDateToYMD = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };

        // Gehe durch jeden Tag im Zeitraum
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateKey = formatDateToYMD(currentDate);

          // Auf Wochenenden prüfen
          const dayOfWeek = currentDate.getDay(); // 0 = Sonntag, 6 = Samstag
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            // Prüfen, ob der Tag im richtigen Monat und Jahr ist
            if (
              currentDate.getMonth() + 1 === month &&
              currentDate.getFullYear() === year
            ) {
              // Prüfen, ob wir diesen Tag bereits gezählt haben
              if (!countedDays.has(dateKey)) {
                if (event.event_type === "vacation") {
                  vacationDaysCount++;
                  countedDays.set(dateKey, "vacation");
                } else if (event.event_type === "sick") {
                  sickDaysCount++;
                  countedDays.set(dateKey, "sick");
                }
              }
            }
          }

          // Inkrementiere das aktuelle Datum um einen Tag
          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

      // Werte speichern
      autoLoadedVacationDays.value = vacationDaysCount;
      autoLoadedSickDays.value = sickDaysCount;

      // Werte in die Eingabefelder übertragen
      vacationDays.value = vacationDaysCount;
      sickDays.value = sickDaysCount;

      // KORRIGIERTE NACHRICHT: Präzisieren, dass nur der aktuelle Monat geladen wurde
      toast.success(
        `${vacationDaysCount} Urlaubstage und ${sickDaysCount} Krankheitstage für ${currentMonthLabel.value} ${selectedYear.value} geladen`
      );
    } else {
      toast.error("Fehler beim Laden der Kalenderdaten");
    }
  } catch (error) {
    toast.error("Verbindungsfehler beim Laden der Kalenderdaten");
  } finally {
    loadingCalendarData.value = false;
  }
}

// ======== Lebenszyklus-Hooks ========
onMounted(async () => {
  // Zuerst die Arbeitszeit-Einstellungen laden
  await fetchUserWorkTimeSettings();

  // Vorhandene Logik beibehalten
  await fetchUser();
  Promise.all([
    fetchZusammenfassungen(),
    fetchPreise(),
    fetchDeviceTypes(),
    fetchDetailedNebentaetigkeiten(),
  ]).catch((error) => {
    console.error("Fehler beim Initialisieren der Daten:", error);
  });

  // Automatisch 3-Monats-Daten laden, wenn Tab "monat" aktiv ist
  if (activeTab.value === "monat") {
    setTimeout(() => {
      if (calendarMonths.value.length === 0) {
        loadThreeMonthCalendarData();
      }
    }, 1000);
  }
});

// ======== Watcher ========
// Watcher für Tab-Wechsel - Kalenderdaten automatisch bei Wechsel zum Berechnungs-Tab laden
watch(activeTab, (newTab) => {
  if (newTab === "monat") {
    // Umstellung: Bei Tab-Wechsel direkt 3-Monats-Daten laden
    // anstatt nur den aktuellen Monat zu laden
    if (calendarMonths.value.length === 0) {
      // Kurze Verzögerung für bessere UI-Erfahrung
      setTimeout(() => {
        loadThreeMonthCalendarData();
      }, 500);
    }
  }
});

// Watcher für Monat/Jahr - Kalenderdaten neu laden bei Änderung
watch([selectedMonth, selectedYear], () => {
  // Bestehenden Code beibehalten
  fetchZusammenfassungen();
  fetchDetailedNebentaetigkeiten();

  // Kalenderdaten zurücksetzen
  autoLoadedVacationDays.value = 0;
  autoLoadedSickDays.value = 0;

  // Zurücksetzen der 3-Monats-Daten
  calendarMonths.value = [];
  activityMonths.value = [];

  // Wenn wir auf dem Berechnungs-Tab sind, neue 3-Monats-Daten laden
  if (activeTab.value === "monat") {
    setTimeout(loadThreeMonthCalendarData, 500);
  }
});
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.app-container {
  max-width: 1200px !important;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.summary-view {
  position: relative;
  width: 100%;
}
.summary-card,
.summary-result-card,
.filter-card,
.device-card,
.nebentaetigkeiten-card,
.overall-earnings-card,
.no-data-card {
  background-color: rgba(30, 30, 35, 0.7) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Hover-Effekte für Karten */
.summary-card:hover,
.summary-result-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

/* ======== Header-Bereiche ======== */
.summary-header,
.result-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  text-align: left;
}

/* Spezielles Farbschema für verschiedene Bereiche */
.earnings-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  text-align: left;
}

.activity-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  text-align: left;
}

.device-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(30, 30, 40, 0.5);
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title,
.earnings-title,
.activity-title,
.device-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  letter-spacing: 0.3px;
  -webkit-background-clip: text;
  background-clip: text;
}

.device-title {
  font-size: 16px;
}

.summary-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

/* ======== Tabs und Filter-Bereiche ======== */
.summary-tabs-wrap {
  margin: 12px 16px 8px;
  padding: 6px;
  background: rgba(20, 20, 30, 0.4);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.summary-tabs {
  background: transparent;
}

.filter-section {
  padding: 16px 20px;
  background: rgba(30, 30, 40, 0.3);
}

.filter-select {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.filter-select:hover {
  background: rgba(35, 35, 45, 0.6) !important;
}

.export-btn {
  height: 42px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2) !important;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.export-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.3) !important;
}

:global(.pdf-preview-overlay) {
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.pdf-preview-shell {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
}

.pdf-preview-card {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden !important;
  background: rgba(28, 28, 34, 0.94) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45) !important;
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(
    180deg,
    rgba(40, 44, 52, 0.9) 0%,
    rgba(28, 30, 38, 0.55) 100%
  );
}

.pdf-preview-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.pdf-preview-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 175, 80, 0.14);
  border: 1px solid rgba(76, 175, 80, 0.28);
  flex-shrink: 0;
}

.pdf-preview-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: rgba(255, 255, 255, 0.94);
}

.pdf-preview-subtitle {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: min(52vw, 520px);
}

.pdf-preview-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pdf-action-btn {
  border-radius: 8px !important;
  letter-spacing: 0.3px;
  text-transform: none !important;
}

.pdf-close-btn {
  opacity: 0.75;
}

.pdf-close-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.06) !important;
}

.pdf-preview-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 14px 14px;
  background:
    radial-gradient(ellipse at top, rgba(76, 175, 80, 0.06), transparent 55%),
    rgba(18, 18, 22, 0.65);
}

.pdf-preview-stage {
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #2a2d33;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.pdf-preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #2a2d33;
}

.pdf-preview-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.45);
  gap: 4px;
}

@media (max-width: 700px) {
  .pdf-preview-shell {
    padding: 8px;
  }

  .pdf-preview-header {
    flex-direction: column;
    align-items: stretch;
  }

  .pdf-preview-actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .pdf-preview-subtitle {
    max-width: 70vw;
  }
}

/* ======== Ergebnis-Bereich ======== */
.result-content {
  padding: 20px;
}

/* ======== Gerätetypen und Statistik-Bereiche ======== */
.section-heading {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
}

.device-card {
  background: rgba(30, 30, 40, 0.4) !important;
  border-radius: 12px;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.device-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.25);
}

/* Spezielles Design für die Berechnungs-Karten */

.device-content {
  padding: 16px;
}

/* ======== Statistik-Gruppen und Items ======== */
.stat-group {
  margin-bottom: 18px;
  position: relative;
}

.stat-group:last-child {
  margin-bottom: 0;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 5px;
  margin: 4px 0;
  border-radius: 6px;
  background: rgba(35, 35, 45, 0.5);
  transition: all 0.2s ease;
  align-items: center;
}

.stat-item:hover {
  background: rgba(40, 40, 50, 0.6);
  transform: translateX(2px);
}

.stat-name {
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
}

.stat-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  background: rgba(25, 25, 35, 0.6);
  border-radius: 6px;
  font-size: 13px;
}

/* Kategoriezusammenfassung */
.category-summary {
  margin-top: 12px;
}

.summary-total {
  margin-top: 0 !important;
  background: rgba(76, 175, 80, 0.15) !important;
  border: 1px solid rgba(76, 175, 80, 0.3);
  padding: 10px 12px !important;
}

.summary-total .stat-name,
.summary-total .stat-value {
  color: rgba(76, 175, 80, 1);
  font-size: 14px;
}

/* Gesamtsumme über alle Kategorien */
.overall-total-card {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.12), rgba(56, 142, 60, 0.08)) !important;
  border: 2px solid rgba(76, 175, 80, 0.4);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
}

.overall-total-content {
  padding: 20px !important;
}

.overall-total-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.overall-total-label {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: rgba(76, 175, 80, 1);
}

.overall-total-value {
  font-size: 18px;
  color: rgba(76, 175, 80, 1);
  background: rgba(76, 175, 80, 0.15);
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

/* Preisanzeige in der Berechnungsansicht */
.price {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.price-unit {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.price-total {
  font-weight: 600;
  color: rgba(76, 175, 80, 0.9);
}

/* Summen und Gesamtbeträge */
.device-total {
  margin-top: 16px;
  padding-top: 10px;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  flex-grow: 1;
  text-align: left;
  min-width: 60%;
}

.total-value {
  font-weight: 700;
  color: rgba(76, 175, 80, 1);
  font-size: 16px;
  background: rgba(76, 175, 80, 0.15);
  padding: 3px 10px;
  border-radius: 8px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* ======== Nebentätigkeiten ======== */
.activity-content {
  padding: 16px;
}

.activity-group {
  margin-bottom: 12px;
}

.activity-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin: 8px 0;
  border-radius: 10px;
  background: rgba(35, 35, 45, 0.5);
  transition: all 0.2s ease;
  border-left: 3px solid rgba(255, 193, 7, 0.8);
}

.activity-group-header {
  cursor: pointer;
}

.activity-group-header:hover {
  transform: translateX(3px);
  background: rgba(40, 40, 50, 0.6);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.activity-item:hover {
  transform: translateX(3px);
  background: rgba(40, 40, 50, 0.6);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.activity-details {
  display: flex;
  align-items: center;
  gap: 4px;
}

.activity-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.activity-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 6px;
}

.activity-duration {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  padding: 3px 8px;
  background: rgba(255, 193, 7, 0.15);
  border-radius: 6px;
  font-size: 13px;
}

/* Expandierte Einzeleinträge */
.activity-items-list {
  margin-left: 35px;
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid rgba(255, 193, 7, 0.3);
}

.activity-item-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  background: rgba(30, 30, 40, 0.4);
  transition: all 0.2s ease;
}

.activity-item-detail:hover {
  background: rgba(35, 35, 45, 0.6);
}

.activity-item-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}

.activity-date {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.activity-time {
  color: rgba(255, 255, 255, 0.6);
}

.activity-duration-small {
  color: rgba(255, 193, 7, 0.8);
  font-weight: 500;
}

.activity-item-actions {
  display: flex;
  gap: 4px;
}

.activity-total {
  margin-top: 20px;
  padding: 16px;
  border-radius: 10px;
  background: rgba(30, 30, 40, 0.5);
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-left: 3px solid rgba(255, 193, 7, 0.8);
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.total-row + .total-row {
  margin-top: 5px;
}

.total-empty {
  flex-grow: 1;
}

.total-primary {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-align: right;
  white-space: nowrap;
}

.total-secondary {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  text-align: right;
  white-space: nowrap;
}

/* ======== Gesamtverdienst-Card ======== */
.earnings-content {
  padding: 16px;
}

.earnings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  margin: 8px 0;
  border-radius: 10px;
  background: rgba(35, 35, 45, 0.5);
}

.earnings-label {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.earnings-value {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.earnings-total {
  margin-top: 16px;
  padding: 16px;
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 3px solid rgba(76, 175, 80, 0.8);
}

.grand-total {
  font-size: 20px;
  font-weight: 700;
  color: rgba(76, 175, 80, 1);
}

/* ======== Keine-Daten-Zustände ======== */
.no-data {
  padding: 14px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-style: italic;
}

.no-data-content {
  padding: 40px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-data-content h3 {
  margin: 16px 0 8px;
  color: rgba(255, 255, 255, 0.7);
}

.no-data-content p {
  color: rgba(255, 255, 255, 0.5);
  max-width: 300px;
}

/* ======== Loading Overlay ======== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 15, 20, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-text {
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 500;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.device-card,
.nebentaetigkeiten-card,
.activity-item,
.overall-earnings-card {
  animation: fadeIn 0.3s ease-out;
}

/* Verzögerte Animation für eine flüssigere Erscheinung */
.device-card:nth-child(2) {
  animation-delay: 0.05s;
}
.device-card:nth-child(3) {
  animation-delay: 0.1s;
}
.device-card:nth-child(4) {
  animation-delay: 0.15s;
}
.device-card:nth-child(5) {
  animation-delay: 0.2s;
}

.activity-item:nth-child(2) {
  animation-delay: 0.05s;
}
.activity-item:nth-child(3) {
  animation-delay: 0.1s;
}
.activity-item:nth-child(4) {
  animation-delay: 0.15s;
}
.activity-item:nth-child(5) {
  animation-delay: 0.2s;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.grand-total {
  animation: pulse 3s infinite;
}

/* ======== Responsive Design ======== */
@media (max-width: 960px) {
  .activity-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .activity-duration {
    align-self: flex-end;
  }

  .earnings-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .earnings-value {
    align-self: flex-end;
  }

  .earnings-total {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .grand-total {
    align-self: flex-end;
  }

  .price {
    flex-direction: column;
    align-items: flex-end;
  }
}

/* Mobile Anpassungen */
@media (max-width: 600px) {
  .summary-header,
  .result-header,
  .device-header,
  .activity-header,
  .earnings-header {
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .card-title,
  .device-title,
  .activity-title,
  .earnings-title {
    font-size: 16px;
  }

  .section-heading {
    font-size: 15px;
  }

  .filter-section {
    padding: 12px;
  }

  .result-content {
    padding: 12px;
  }

  .stat-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .stat-value {
    margin-top: 5px;
    align-self: flex-end;
  }

  .device-total {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .total-value {
    align-self: flex-end;
  }

  .activity-total {
    padding: 12px;
  }

  .total-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .total-primary,
  .total-secondary {
    align-self: flex-end;
    margin-top: 4px;
  }
}

/* ======== Scrollbar-Styling ======== */
.summary-view::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.summary-view::-webkit-scrollbar-track {
  background: transparent;
}

.summary-view::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.summary-view::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

.average-earnings-section {
  background: rgba(25, 45, 65, 0.3);
  border-radius: 10px;
  padding: 16px;
  margin-top: 24px;
  border-left: 3px solid rgba(76, 175, 80, 0.8);
}

.average-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 16px;
  font-size: 15px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}

.average-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: rgba(30, 30, 40, 0.4);
  border-radius: 8px;
  margin: 8px 0;
}

.month-label {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

.month-value {
  font-weight: 500;
}

.average-total {
  margin-top: 16px;
  padding: 14px 12px;
  border-radius: 8px;
  background: rgba(76, 175, 80, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 3px solid rgba(76, 175, 80, 0.8);
}

.average-value {
  color: rgba(76, 175, 80, 1) !important;
  font-size: 18px;
  background: rgba(76, 175, 80, 0.15);
  animation: none !important; /* Pulsieren nur beim Gesamtbetrag */
}

.average-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
}

/* Anpassung für Mobile */
@media (max-width: 600px) {
  .average-item,
  .average-total {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .month-value,
  .average-value {
    align-self: flex-end;
  }
}

.side-activities {
  border-left: 3px solid rgba(255, 193, 7, 0.8) !important;
}

.side-value {
  color: rgba(255, 193, 7, 0.9) !important;
}

.tooltip-content {
  padding: 8px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
}

.tooltip-note {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
  font-style: italic;
}

/* Styles für Arbeitszeitkonfiguration */
.worktime-config {
  background: rgba(30, 35, 45, 0.4);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 3px solid rgba(255, 193, 7, 0.7);
}

.config-title {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
}

.config-inputs {
  margin-bottom: 8px;
}

.input-group {
  display: flex;
  gap: 16px;
}

.time-input {
  flex: 1;
}

.workdays-info {
  background: rgba(20, 25, 35, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin: 6px 0;
  font-size: 13px;
}

.info-label {
  color: rgba(255, 255, 255, 0.7);
}

.info-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.highlight {
  padding: 8px;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 6px;
  margin-top: 8px;
  border-left: 2px solid rgba(255, 193, 7, 0.7);
}

.highlight .info-value {
  color: rgba(255, 193, 7, 0.9);
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 600px) {
  .input-group {
    flex-direction: column;
    gap: 12px;
  }
}

/* Neue Styles für die Berechnungsschritte */
.calculation-steps {
  margin-top: 16px;
  background: rgba(35, 30, 40, 0.3);
  border-radius: 8px;
  padding: 12px;
  border-left: 2px solid rgba(255, 193, 7, 0.5);
}

.step-title {
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(255, 193, 7, 0.9);
  display: flex;
  align-items: center;
  font-size: 13px;
}

.steps-list {
  margin: 0;
  padding-left: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.steps-list li {
  margin: 4px 0;
}

.final-step {
  font-weight: 600;
  color: rgba(255, 193, 7, 0.9);
  margin-top: 8px !important;
  background: rgba(255, 193, 7, 0.1);
  padding: 4px 6px;
  border-radius: 4px;
}

/* Styles für die neuen Kalender-Elemente */
.special-input-item {
  align-items: center;
}

.special-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.days-input {
  max-width: 80px;
  text-align: center;
}

.calendar-badge {
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.action-item {
  margin-top: 10px;
  justify-content: flex-end;
}

.load-calendar-btn {
  width: 100%;
  margin-bottom: 12px;
  border-style: dashed;
  background: rgba(76, 175, 80, 0.05);
}

.load-calendar-btn:hover {
  background: rgba(76, 175, 80, 0.1);
}

/* Tooltip für die Berechnung */
.calculation-tooltip {
  max-width: 300px;
  padding: 10px;
}

.calculation-tooltip h4 {
  margin-bottom: 8px;
  font-size: 14px;
}

.calculation-tooltip ol {
  margin: 0;
  padding-left: 20px;
}

.calculation-tooltip li {
  margin-bottom: 6px;
  font-size: 12px;
}

.final-formula {
  font-weight: 600;
  padding: 4px;
  border-radius: 4px;
  background: rgba(76, 175, 80, 0.1);
  margin-top: 8px !important;
}

:deep(.v-field__prepend-inner .v-icon) {
  color: rgb(76, 175, 80) !important;
}

/* Tab-Button Styling – keine fixe Höhe, sonst schneidet Vuetify den Inhalt ab */
.summary-tabs :deep(.v-tab) {
  min-width: 160px;
  border-radius: 6px !important;
  transition: all 0.3s ease;
  opacity: 0.8;
  margin: 0 2px;
  text-transform: none;
  letter-spacing: normal;
}

.summary-tabs :deep(.v-tab:hover) {
  background: rgba(255, 255, 255, 0.05);
  opacity: 1;
}

.summary-tabs :deep(.v-tab--selected) {
  background: rgba(76, 175, 80, 0.15) !important;
  opacity: 1;
}

.summary-tabs :deep(.v-tab--selected .v-icon) {
  color: rgba(76, 175, 80, 1) !important;
}

/* ======== Verbesserte Abschnitte ======== */
.content-section {
  background: rgba(30, 35, 45, 0.4);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.content-section:nth-child(2) {
  border-left-color: rgba(33, 150, 243, 0.7);
}

.content-section:nth-child(3) {
  border-left-color: rgba(255, 193, 7, 0.7);
}

.section-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
}

/* ======== Abwesenheiten-Eingabekarte ======== */
.absence-input-card {
  background: rgba(25, 30, 40, 0.5) !important;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.absence-input-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(30, 35, 45, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 14px;
  font-weight: 500;
}

.absence-input-content {
  padding: 16px;
}

.absence-input-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.absence-input-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.absence-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.absence-field {
  display: flex;
  align-items: center;
  gap: 12px;
}

.absence-input {
  max-width: 80px;
}

/* Anpassungen für mobile Ansicht */
@media (max-width: 600px) {
  .absence-input-group {
    flex-direction: column;
    gap: 12px;
  }

  .absence-input-header {
    flex-wrap: wrap;
    gap: 8px;
  }
}

/* Stiländerungen für bessere Lesbarkeit */
.average-content {
  background: rgba(25, 30, 40, 0.3);
  border-radius: 8px;
  padding: 12px;
}

.average-item {
  background: rgba(35, 40, 50, 0.4);
}

.workdays-info {
  background: rgba(25, 30, 40, 0.3);
}

/* Neue Styles für die Abwesenheiten-Integration */
.workdays-calculation {
  margin-bottom: 20px;
}

.absence-overview,
.calculation-details {
  height: 100%;
  background: rgba(25, 30, 40, 0.4);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.absence-header,
.details-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(30, 35, 45, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 15px;
  font-weight: 500;
}

.absence-title,
.details-title {
  display: flex;
  align-items: center;
}

.absence-content {
  padding: 16px;
}

.absence-type {
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(35, 40, 50, 0.4);
  border-radius: 8px;
  border-left: 3px solid transparent;
}

.absence-type:first-child {
  border-left-color: rgba(255, 193, 7, 0.7); /* Urlaub */
}

.absence-type:last-child {
  border-left-color: rgba(244, 67, 54, 0.7); /* Krankheit */
  margin-bottom: 0;
}

.absence-type-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  margin-bottom: 10px;
  font-size: 14px;
}

.absence-type-input {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.absence-input {
  max-width: 100px;
}

.absence-badge {
  font-size: 11px;
  font-weight: 400;
  height: 24px !important;
}

/* Überarbeitete Info-Items */
.workdays-info {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(35, 40, 50, 0.4);
  transition: all 0.2s ease;
}

.info-item.with-badge {
  background: rgba(35, 40, 50, 0.5);
}

.info-item.highlighted {
  background: rgba(33, 150, 243, 0.1);
  border-left: 3px solid rgba(33, 150, 243, 0.7);
}

.info-item.highlight {
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid rgba(255, 193, 7, 0.7);
  padding: 12px;
  margin-top: 8px;
}

/* Anpassungen für kleine Bildschirme */
@media (max-width: 960px) {
  .absence-overview,
  .calculation-details {
    margin-bottom: 16px;
  }

  .absence-type-input {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .absence-input {
    max-width: 100%;
    width: 100%;
  }

  .absence-badge {
    align-self: flex-end;
  }
}

/* Neue Styles für die erweiterte 3-Monats-Berechnung */
.absence-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.absence-month {
  background: rgba(35, 40, 50, 0.4);
  border-radius: 8px;
  overflow: hidden;
}

.month-header {
  background: rgba(30, 35, 45, 0.6);
  padding: 8px 12px;
  font-weight: 500;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.month-absences {
  padding: 10px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.absence-type.compact {
  margin-bottom: 0;
  padding: 8px;
  background: rgba(35, 40, 50, 0.7);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  min-width: 120px;
}

.absence-type-value {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.info-section-header {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  padding: 8px 0;
  margin-top: 5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.activity-distribution {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}

.activity-month {
  background: rgba(35, 40, 50, 0.5);
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.month-label {
  font-weight: 500;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.month-activities {
  display: flex;
  gap: 16px;
}

.activity-count,
.activity-hours {
  display: flex;
  gap: 5px;
  font-size: 12px;
}

.count-label,
.hours-label {
  color: rgba(255, 255, 255, 0.7);
}

.count-value,
.hours-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* Styling für die Arbeitszeit-Info-Badge */
.worktime-info-badge {
  display: flex;
  align-items: center;
  background-color: rgba(25, 118, 210, 0.15);
  border-left: 3px solid rgba(25, 118, 210, 0.7);
  padding: 10px 16px;
  border-radius: 6px;
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
}

.worktime-info-badge strong {
  color: rgba(255, 255, 255, 0.95);
  margin: 0 2px;
}

@media (max-width: 600px) {
  .worktime-info-badge {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
  }

  .worktime-info-badge span {
    margin-top: 6px;
  }
}
</style>
